import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import SALARY_DATA from './salaryData';
import TradeMachine from './TradeMachine';
import DraftBoard from './DraftBoard';
import DraftSim from './DraftSim';
import FreeAgencyBoard from './FreeAgencyBoard';
import SeasonHub from './SeasonHub';
import { generateSchedule, computeStandings, getPlayoffSeeds } from './scheduleGenerator';
import { quickSim } from './gameSimulator';
import { CBA } from './tradeEngine';
import { analyzeWaive, executeWaive as executeWaiveFA, initPool, addToPool, filterRosteredPlayers } from './freeAgentPool';
import { advanceSeason, buildRookieContract } from './seasonAdvance';
import { generateOffers } from './contractMarket';
import DRAFT_PICKS from './draftPicks';
import { DRAFT_ORDER_2026, getDraftProspects } from './draftProspects';
import { getPlayerRating, getRatingColor, getBasePlayerRating } from './playerRatings';
import { progressPlayer, getPlayerPotential } from './playerPotentials';
import { generateAITrades, executeAITrades, generateCounterOffers, executeCounterOffer } from './aiTrades';
import { getPlayerAgeSafe, registerDraftClass } from './playerAges';
import PLAYER_POSITIONS from './playerPositions';
import DepthChart, { getEffectiveOVR } from './DepthChart';
import './App.css';

// ============================================================================
// CONFIG
// ============================================================================
const API_KEY = 'b40d79d5-2df8-455b-9da2-0b86020972d3';
const API_BASE = 'https://api.balldontlie.io/nba/v1';
const CURRENT_SEASON = 2025;

// ============================================================================
// API
// ============================================================================
const cache = {};
const CACHE_DURATION = 24 * 60 * 60 * 1000;

const api = async (endpoint, params = {}) => {
  const url = new URL(`${API_BASE}${endpoint}`);
  Object.entries(params).forEach(([k, v]) => {
    if (v != null && v !== '') {
      if (Array.isArray(v)) v.forEach(item => url.searchParams.append(`${k}[]`, item));
      else url.searchParams.set(k, v);
    }
  });
  const cacheKey = url.toString();
  const cached = cache[cacheKey];
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) return cached.data;
  const res = await fetch(url.toString(), { headers: { Authorization: API_KEY } });
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  const data = await res.json();
  cache[cacheKey] = { data, timestamp: Date.now() };
  return data;
};

// ============================================================================
// TEAMS
// ============================================================================
const TEAMS = [
  { abbr: 'ATL', name: 'Atlanta Hawks', conference: 'East', division: 'Southeast', bdlId: 1, nbaId: 1610612737 },
  { abbr: 'BOS', name: 'Boston Celtics', conference: 'East', division: 'Atlantic', bdlId: 2, nbaId: 1610612738 },
  { abbr: 'BKN', name: 'Brooklyn Nets', conference: 'East', division: 'Atlantic', bdlId: 3, nbaId: 1610612751 },
  { abbr: 'CHA', name: 'Charlotte Hornets', conference: 'East', division: 'Southeast', bdlId: 4, nbaId: 1610612766 },
  { abbr: 'CHI', name: 'Chicago Bulls', conference: 'East', division: 'Central', bdlId: 5, nbaId: 1610612741 },
  { abbr: 'CLE', name: 'Cleveland Cavaliers', conference: 'East', division: 'Central', bdlId: 6, nbaId: 1610612739 },
  { abbr: 'DAL', name: 'Dallas Mavericks', conference: 'West', division: 'Southwest', bdlId: 7, nbaId: 1610612742 },
  { abbr: 'DEN', name: 'Denver Nuggets', conference: 'West', division: 'Northwest', bdlId: 8, nbaId: 1610612743 },
  { abbr: 'DET', name: 'Detroit Pistons', conference: 'East', division: 'Central', bdlId: 9, nbaId: 1610612765 },
  { abbr: 'GSW', name: 'Golden State Warriors', conference: 'West', division: 'Pacific', bdlId: 10, nbaId: 1610612744 },
  { abbr: 'HOU', name: 'Houston Rockets', conference: 'West', division: 'Southwest', bdlId: 11, nbaId: 1610612745 },
  { abbr: 'IND', name: 'Indiana Pacers', conference: 'East', division: 'Central', bdlId: 12, nbaId: 1610612754 },
  { abbr: 'LAC', name: 'LA Clippers', conference: 'West', division: 'Pacific', bdlId: 13, nbaId: 1610612746 },
  { abbr: 'LAL', name: 'Los Angeles Lakers', conference: 'West', division: 'Pacific', bdlId: 14, nbaId: 1610612747 },
  { abbr: 'MEM', name: 'Memphis Grizzlies', conference: 'West', division: 'Southwest', bdlId: 15, nbaId: 1610612763 },
  { abbr: 'MIA', name: 'Miami Heat', conference: 'East', division: 'Southeast', bdlId: 16, nbaId: 1610612748 },
  { abbr: 'MIL', name: 'Milwaukee Bucks', conference: 'East', division: 'Central', bdlId: 17, nbaId: 1610612749 },
  { abbr: 'MIN', name: 'Minnesota Timberwolves', conference: 'West', division: 'Northwest', bdlId: 18, nbaId: 1610612750 },
  { abbr: 'NOP', name: 'New Orleans Pelicans', conference: 'West', division: 'Southwest', bdlId: 19, nbaId: 1610612740 },
  { abbr: 'NYK', name: 'New York Knicks', conference: 'East', division: 'Atlantic', bdlId: 20, nbaId: 1610612752 },
  { abbr: 'OKC', name: 'Oklahoma City Thunder', conference: 'West', division: 'Northwest', bdlId: 21, nbaId: 1610612760 },
  { abbr: 'ORL', name: 'Orlando Magic', conference: 'East', division: 'Southeast', bdlId: 22, nbaId: 1610612753 },
  { abbr: 'PHI', name: 'Philadelphia 76ers', conference: 'East', division: 'Atlantic', bdlId: 23, nbaId: 1610612755 },
  { abbr: 'PHX', name: 'Phoenix Suns', conference: 'West', division: 'Pacific', bdlId: 24, nbaId: 1610612756 },
  { abbr: 'POR', name: 'Portland Trail Blazers', conference: 'West', division: 'Northwest', bdlId: 25, nbaId: 1610612757 },
  { abbr: 'SAC', name: 'Sacramento Kings', conference: 'West', division: 'Pacific', bdlId: 26, nbaId: 1610612758 },
  { abbr: 'SAS', name: 'San Antonio Spurs', conference: 'West', division: 'Southwest', bdlId: 27, nbaId: 1610612759 },
  { abbr: 'TOR', name: 'Toronto Raptors', conference: 'East', division: 'Atlantic', bdlId: 28, nbaId: 1610612761 },
  { abbr: 'UTA', name: 'Utah Jazz', conference: 'West', division: 'Northwest', bdlId: 29, nbaId: 1610612762 },
  { abbr: 'WAS', name: 'Washington Wizards', conference: 'East', division: 'Southeast', bdlId: 30, nbaId: 1610612764 },
];

const getTeam = (abbr) => TEAMS.find(t => t.abbr === abbr);

// CBA imported from tradeEngine.js

// ============================================================================
// UTILITIES
// ============================================================================
const teamLogoUrl = (nbaId) => `https://cdn.nba.com/logos/nba/${nbaId}/global/L/logo.svg`;
const playerHeadshotUrl = (nbaComId) => `https://cdn.nba.com/headshots/nba/latest/1040x760/${nbaComId}.png`;
const formatSalary = (n) => {
  if (!n && n !== 0) return '\u2014';
  if (n >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `$${(n / 1000).toFixed(0)}K`;
  return `$${n.toLocaleString()}`;
};
const formatSalaryFull = (n) => n ? `$${n.toLocaleString()}` : '\u2014';
const formatPosition = (pos) => {
  if (!pos) return '\u2014';
  const map = { 'G': 'G', 'F': 'F', 'C': 'C', 'G-F': 'G/F', 'F-G': 'G/F', 'F-C': 'F/C', 'C-F': 'F/C' };
  return map[pos] || pos;
};
const getInitials = (name) => {
  const parts = name.split(' ').filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// ============================================================================
// PLAYER HEADSHOT COMPONENT (with initials fallback)
// ============================================================================
// Some NBA.com headshot IDs serve wrong photos — force initials fallback for these
const HEADSHOT_BLACKLIST = new Set([1642259, 1642855, 1631169]); // Alex Sarr, Noa Essengue, Josh Minott

const PlayerHeadshot = ({ name, nbaComId, className = 'player-headshot', initialsClass = 'player-initials' }) => {
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);

  if (failed || !nbaComId || HEADSHOT_BLACKLIST.has(nbaComId)) {
    return <div className={initialsClass}>{getInitials(name)}</div>;
  }

  return (
    <>
      {!loaded && <div className={initialsClass} style={{ position: 'absolute' }}>{getInitials(name)}</div>}
      <img
        src={playerHeadshotUrl(nbaComId)}
        alt={name}
        className={className}
        style={loaded ? {} : { opacity: 0, position: 'absolute' }}
        onLoad={() => setLoaded(true)}
        onError={() => setFailed(true)}
      />
    </>
  );
};

// ============================================================================
// DATA HOOKS
// ============================================================================
const useStandings = () => {
  const [standings, setStandings] = useState({});
  const [loading, setLoading] = useState(true);
  const fetched = useRef(false);
  useEffect(() => {
    if (fetched.current) return;
    fetched.current = true;
    (async () => {
      try {
        const res = await api('/standings', { season: CURRENT_SEASON });
        const map = {};
        (res.data || []).forEach(s => {
          const abbr = s.team?.abbreviation;
          if (abbr) map[abbr] = { wins: s.wins || 0, losses: s.losses || 0, confRank: s.conference_rank };
        });
        setStandings(map);
      } catch (e) { console.error('Standings error:', e); }
      setLoading(false);
    })();
  }, []);
  return { standings, loading };
};

const useRoster = (teamAbbr, salaryDataOverride) => {
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!teamAbbr) return;
    setLoading(true);
    setError(null);
    const team = getTeam(teamAbbr);
    if (!team) { setError('Team not found'); setLoading(false); return; }
    const activeSalaryData = salaryDataOverride || SALARY_DATA;
    const salaryTeam = activeSalaryData[teamAbbr];

    (async () => {
      try {
        const res = await api('/players/active', { team_ids: [team.bdlId], per_page: 100 });
        const apiPlayers = res.data || [];

        if (salaryTeam) {
          const players = salaryTeam.players.map((sp) => {
            const clean = (s) => s.toLowerCase().replace(/[^a-z]/g, '');
            const apiMatch = apiPlayers.find(ap => {
              const apiName = clean(`${ap.first_name}${ap.last_name}`);
              const spName = clean(sp.name);
              if (apiName === spName) return true;
              const apiLast = clean(ap.last_name);
              const spLast = clean(sp.name.split(' ').pop());
              return apiLast === spLast && apiLast.length > 3;
            });
            return {
              name: sp.name, nbaComId: sp.nbaComId,
              bdlId: apiMatch?.id || null,
              position: apiMatch ? formatPosition(apiMatch.position) : '\u2014',
              jerseyNumber: apiMatch?.jersey_number || '\u2014',
              height: apiMatch?.height || '\u2014',
              weight: apiMatch?.weight ? `${apiMatch.weight} lbs` : '\u2014',
              college: apiMatch?.college || '\u2014',
              country: apiMatch?.country || '\u2014',
              draftYear: apiMatch?.draft_year || null,
              draftRound: apiMatch?.draft_round || null,
              draftNumber: apiMatch?.draft_number || null,
              salary2526: sp.salary2526, salary2627: sp.salary2627,
              salary2728: sp.salary2728, salary2829: sp.salary2829,
              option: sp.option, isTwoWay: sp.isTwoWay || false,
            };
          });
          setRoster(players);
        } else {
          const players = apiPlayers.map(p => ({
            name: `${p.first_name} ${p.last_name}`, nbaComId: null,
            bdlId: p.id,
            position: formatPosition(p.position),
            jerseyNumber: p.jersey_number || '\u2014',
            height: p.height || '\u2014',
            weight: p.weight ? `${p.weight} lbs` : '\u2014',
            college: p.college || '\u2014', country: p.country || '\u2014',
            draftYear: p.draft_year || null, draftRound: p.draft_round || null, draftNumber: p.draft_number || null,
            salary2526: 0, salary2627: 0, salary2728: 0, salary2829: 0,
            option: '', isTwoWay: false,
          }));
          players.sort((a, b) => (parseInt(a.jerseyNumber) || 999) - (parseInt(b.jerseyNumber) || 999));
          setRoster(players);
        }
      } catch (e) {
        if (salaryTeam) {
          setRoster(salaryTeam.players.map(sp => ({
            name: sp.name, nbaComId: sp.nbaComId, bdlId: null,
            position: '\u2014', jerseyNumber: '\u2014', height: '\u2014', weight: '\u2014',
            college: '\u2014', country: '\u2014',
            draftYear: null, draftRound: null, draftNumber: null,
            salary2526: sp.salary2526, salary2627: sp.salary2627,
            salary2728: sp.salary2728, salary2829: sp.salary2829,
            option: sp.option, isTwoWay: sp.isTwoWay || false,
          })));
        } else { setError('Failed to load roster.'); }
      }
      setLoading(false);
    })();
  }, [teamAbbr, salaryDataOverride]);

  return { roster, loading, error };
};

// ============================================================================
// PLAYER MODAL
// ============================================================================
const PlayerModal = ({ player, teamAbbr, onClose, onWaive, onExtend }) => {
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [contractOpen, setContractOpen] = useState(false);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [extYears, setExtYears] = useState(2);
  const [extSalary, setExtSalary] = useState(0);
  const team = getTeam(teamAbbr);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let playerId = player?.bdlId;

      if (!playerId) {
        try {
          const lastName = player.name.split(' ').pop();
          const searchRes = await api('/players/active', { search: lastName, per_page: 25 });
          if (searchRes.data) {
            const clean = (s) => s.toLowerCase().replace(/[^a-z]/g, '');
            const match = searchRes.data.find(p => {
              const apiName = clean(`${p.first_name}${p.last_name}`);
              const target = clean(player.name);
              return apiName === target;
            });
            if (match) playerId = match.id;
          }
        } catch (e) { /* search failed */ }
      }

      if (!playerId) { if (!cancelled) setStatsLoading(false); return; }

      try {
        const res = await api('/season_averages', { season: CURRENT_SEASON, player_id: playerId });
        if (!cancelled) {
          const statsData = res.data || res;
          if (Array.isArray(statsData) && statsData.length > 0) setStats(statsData[0]);
          else if (statsData && !Array.isArray(statsData) && statsData.pts !== undefined) setStats(statsData);
        }
      } catch (e) {
        try {
          const res2 = await api('/season_averages', { season: CURRENT_SEASON, player_ids: [playerId] });
          if (!cancelled) {
            const d = res2.data || res2;
            if (Array.isArray(d) && d.length > 0) setStats(d[0]);
          }
        } catch (e2) { /* both failed */ }
      }
      if (!cancelled) setStatsLoading(false);
    })();
    return () => { cancelled = true; };
  }, [player]);

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  if (!player) return null;

  const hasSalary = player.salary2526 > 0;
  const yearsRemaining = [player.salary2526, player.salary2627, player.salary2728, player.salary2829]
    .filter(s => s > 0).length;

  // Calculate experience from draft year
  const draftYear = player.draftYear;
  const seasonsPlayed = draftYear ? (2026 - draftYear) : null;
  const expLabel = seasonsPlayed ? (seasonsPlayed === 1 ? 'Rookie' : `Year ${seasonsPlayed}`) : null;

  return (
    <div className="player-modal-overlay" onClick={onClose}>
      <div className="player-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="player-modal-close" onClick={onClose}>&times;</button>

        <div className="player-modal-header">
          <PlayerHeadshot
            name={player.name}
            nbaComId={player.nbaComId}
            className="player-modal-headshot"
            initialsClass="player-modal-initials-lg"
          />
          <div style={{ flex: 1 }}>
            <div className="player-modal-name">{player.name}</div>
            <div className="player-modal-meta">
              #{player.jerseyNumber} &middot; {player.position} &middot; {team?.name}
              {player.isTwoWay ? ' \u00B7 Two-Way' : ''}
            </div>
            <div className="player-modal-meta" style={{ marginTop: 2 }}>
              {expLabel || ''}
              {expLabel && player.country && player.country !== '\u2014' ? ' \u00B7 ' : ''}
              {player.country !== '\u2014' ? player.country : ''}
            </div>
          </div>
          <div style={{ textAlign: 'center', marginRight: 28 }}>
            <span className="ovr-badge" style={{ borderLeftColor: getRatingColor(player.ovr || getPlayerRating(player.name)), fontSize: 13, padding: '3px 8px 3px 10px' }}>
              {player.ovr || getPlayerRating(player.name)}
            </span>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 7, letterSpacing: 1.5, color: 'var(--text-dim)', marginTop: 3 }}>OVR</div>
          </div>
        </div>

        <div className="player-modal-body">
          {/* Season Stats */}
          <div className="player-modal-section-title">2025-26 Season Averages</div>
          {statsLoading ? (
            <div className="player-stats-loading">Loading stats...</div>
          ) : stats ? (
            <div className="player-stats-grid">
              <div className="player-stat-box">
                <div className="player-stat-value">{stats.pts?.toFixed(1) || '0.0'}</div>
                <div className="player-stat-label">PPG</div>
              </div>
              <div className="player-stat-box">
                <div className="player-stat-value">{stats.reb?.toFixed(1) || '0.0'}</div>
                <div className="player-stat-label">RPG</div>
              </div>
              <div className="player-stat-box">
                <div className="player-stat-value">{stats.ast?.toFixed(1) || '0.0'}</div>
                <div className="player-stat-label">APG</div>
              </div>
              <div className="player-stat-box">
                <div className="player-stat-value">{stats.games_played || 0}</div>
                <div className="player-stat-label">GP</div>
              </div>
              <div className="player-stat-box">
                <div className="player-stat-value">{stats.fg_pct ? (stats.fg_pct * 100).toFixed(1) : '0.0'}%</div>
                <div className="player-stat-label">FG%</div>
              </div>
              <div className="player-stat-box">
                <div className="player-stat-value">{stats.fg3_pct ? (stats.fg3_pct * 100).toFixed(1) : '0.0'}%</div>
                <div className="player-stat-label">3P%</div>
              </div>
              <div className="player-stat-box">
                <div className="player-stat-value">{stats.stl?.toFixed(1) || '0.0'}</div>
                <div className="player-stat-label">STL</div>
              </div>
              <div className="player-stat-box">
                <div className="player-stat-value">{stats.blk?.toFixed(1) || '0.0'}</div>
                <div className="player-stat-label">BLK</div>
              </div>
              <div className="player-stat-box">
                <div className="player-stat-value">{stats.turnover?.toFixed(1) || '0.0'}</div>
                <div className="player-stat-label">TOV</div>
              </div>
              <div className="player-stat-box">
                <div className="player-stat-value">{stats.ft_pct ? (stats.ft_pct * 100).toFixed(1) : '0.0'}%</div>
                <div className="player-stat-label">FT%</div>
              </div>
              <div className="player-stat-box">
                <div className="player-stat-value">{stats.min ? parseFloat(stats.min).toFixed(1) : '0.0'}</div>
                <div className="player-stat-label">MPG</div>
              </div>
              <div className="player-stat-box">
                <div className="player-stat-value">{stats.fg3m?.toFixed(1) || '0.0'}</div>
                <div className="player-stat-label">3PM</div>
              </div>
            </div>
          ) : (
            <div className="player-stats-loading">No stats available this season</div>
          )}

          {/* Player Info - Compact */}
          <div className="player-modal-section-title">Player Info</div>
          <div className="player-bio-row">
            <span className="player-bio-label">Height</span>
            <span className="player-bio-value">{player.height}</span>
          </div>
          <div className="player-bio-row">
            <span className="player-bio-label">Weight</span>
            <span className="player-bio-value">{player.weight}</span>
          </div>
          {seasonsPlayed && (
            <div className="player-bio-row">
              <span className="player-bio-label">Experience</span>
              <span className="player-bio-value">
                {seasonsPlayed === 1 ? 'Rookie' : `${seasonsPlayed} seasons`}
                {draftYear ? ` (${draftYear} Draft${player.draftRound ? `, R${player.draftRound}` : ''}${player.draftNumber ? ` #${player.draftNumber}` : ''})` : ''}
              </span>
            </div>
          )}
          <div className="player-bio-row">
            <span className="player-bio-label">College</span>
            <span className="player-bio-value">{player.college}</span>
          </div>

          {/* Contract - Collapsible */}
          {hasSalary && (
            <>
              <button className="player-contract-toggle" onClick={() => setContractOpen(!contractOpen)}>
                <span>Contract &middot; {formatSalary(player.salary2526)}/yr &middot; {yearsRemaining}yr{yearsRemaining > 1 ? 's' : ''}</span>
                <span className={`player-contract-arrow ${contractOpen ? 'open' : ''}`}>&#9662;</span>
              </button>
              {contractOpen && (
                <div style={{ paddingTop: 6 }}>
                  <div className="player-bio-row">
                    <span className="player-bio-label">2025-26</span>
                    <span className="player-bio-value" style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>
                      {formatSalaryFull(player.salary2526)}
                    </span>
                  </div>
                  {player.salary2627 > 0 && (
                    <div className="player-bio-row">
                      <span className="player-bio-label">2026-27</span>
                      <span className="player-bio-value" style={{ fontFamily: 'var(--font-mono)' }}>
                        {formatSalaryFull(player.salary2627)}
                      </span>
                    </div>
                  )}
                  {player.salary2728 > 0 && (
                    <div className="player-bio-row">
                      <span className="player-bio-label">2027-28</span>
                      <span className="player-bio-value" style={{ fontFamily: 'var(--font-mono)' }}>
                        {formatSalaryFull(player.salary2728)}
                      </span>
                    </div>
                  )}
                  {player.salary2829 > 0 && (
                    <div className="player-bio-row">
                      <span className="player-bio-label">2028-29</span>
                      <span className="player-bio-value" style={{ fontFamily: 'var(--font-mono)' }}>
                        {formatSalaryFull(player.salary2829)}
                      </span>
                    </div>
                  )}
                  {player.option && (
                    <div className="player-bio-row">
                      <span className="player-bio-label">Option</span>
                      <span className="player-bio-value">{player.option}</span>
                    </div>
                  )}
                  {!player.salary2627 && (
                    <div className="player-bio-row">
                      <span className="player-bio-label">Status</span>
                      <span className="player-bio-value" style={{ color: 'var(--accent-red)' }}>Unrestricted Free Agent</span>
                    </div>
                  )}
                  {/* Extension Eligibility Notice */}
                  {(() => {
                    const ext = getExtensionStatus(player);
                    if (!ext) return null;
                    return (
                      <div className="ext-notice" style={{ borderLeftColor: ext.color }}>
                        <div className="ext-notice-header">
                          <span className="ext-badge" style={{ borderColor: ext.color, color: ext.color }}>{ext.label}</span>
                          {ext.type === 'rookie' && <span className="ext-notice-type">Rookie Scale</span>}
                          {ext.type === 'veteran' && <span className="ext-notice-type">Veteran</span>}
                          {ext.type === 'expiring' && <span className="ext-notice-type">Expiring</span>}
                        </div>
                        <div className="ext-notice-desc">{ext.desc}</div>
                        <div className="ext-notice-deadline">{ext.deadline}</div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </>
          )}

          {/* Waive Button */}
          {onWaive && hasSalary && !player.isDead && (
            <button className="waive-btn" onClick={() => { onClose(); onWaive(player); }}>
              Waive Player
            </button>
          )}

          {/* Extend Button */}
          {onExtend && hasSalary && !player.isDead && (() => {
            const ext = getExtensionStatus(player);
            if (!ext) return null;
            return (
              <button className="extend-btn" onClick={() => {
                const currentSalary = player.salary2627 || player.salary2526 || 10000000;
                setExtSalary(Math.round(currentSalary * 1.1));
                setExtYears(2);
                setShowExtendModal(true);
              }}>
                Extend Contract
              </button>
            );
          })()}

          {/* Extension Modal */}
          {showExtendModal && (() => {
            const currentSal = player.salary2526 || 0;
            const maxExt = Math.round(currentSal * 1.4);
            const minExt = Math.round(currentSal * 0.5);
            return (
            <div className="ext-modal-overlay" onClick={() => setShowExtendModal(false)}>
              <div className="ext-modal" onClick={e => e.stopPropagation()}>
                <div className="ext-modal-header">
                  <div className="fa-offer-modal-label">Contract Extension</div>
                  <div className="fa-offer-modal-name" style={{ fontSize: 18 }}>{player.name}</div>
                </div>
                <div className="ext-modal-body">
                  <div className="fa-offer-section-title">Extension Terms</div>
                  <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text-dim)', marginBottom: 4 }}>YEARS</div>
                      <select className="fa-sort-select" value={extYears} onChange={e => setExtYears(parseInt(e.target.value))} style={{ width: '100%', padding: 8 }}>
                        {[1, 2, 3, 4].map(y => <option key={y} value={y}>{y} year{y > 1 ? 's' : ''}</option>)}
                      </select>
                    </div>
                    <div style={{ flex: 2 }}>
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text-dim)', marginBottom: 4 }}>ANNUAL SALARY</div>
                      <input
                        type="number"
                        className="draft-search"
                        value={extSalary}
                        onChange={e => setExtSalary(parseInt(e.target.value) || 0)}
                        style={{ width: '100%', marginBottom: 6 }}
                      />
                      <input
                        type="range"
                        min={minExt || 1000000}
                        max={maxExt || 50000000}
                        step={250000}
                        value={extSalary}
                        onChange={e => setExtSalary(parseInt(e.target.value))}
                        className="ext-salary-slider"
                        style={{ width: '100%' }}
                      />
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: 'var(--font-mono)', fontSize: 8, color: 'var(--text-dim)' }}>
                        <span>{formatSalary(minExt || 1000000)}</span>
                        <span>{formatSalary(maxExt || 50000000)}</span>
                      </div>
                    </div>
                  </div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-muted)', padding: '8px 0', borderTop: '1px solid var(--border)' }}>
                    <div>Year 1: {formatSalary(extSalary)}</div>
                    {extYears >= 2 && <div>Year 2: {formatSalary(Math.round(extSalary * 1.05))}</div>}
                    {extYears >= 3 && <div>Year 3: {formatSalary(Math.round(extSalary * 1.05 * 1.05))}</div>}
                    {extYears >= 4 && <div>Year 4: {formatSalary(Math.round(extSalary * Math.pow(1.05, 3)))}</div>}
                    <div style={{ fontWeight: 600, marginTop: 4 }}>
                      Total: {formatSalary(Array.from({ length: extYears }, (_, i) => Math.round(extSalary * Math.pow(1.05, i))).reduce((a, b) => a + b, 0))}
                    </div>
                  </div>
                </div>
                <div className="fa-offer-modal-actions">
                  <button className="waive-cancel-btn" onClick={() => setShowExtendModal(false)}>Cancel</button>
                  <button className="fa-offer-confirm-btn" onClick={() => {
                    onExtend(teamAbbr, player.name, extYears, extSalary);
                    setShowExtendModal(false);
                    onClose();
                  }}>Confirm Extension</button>
                </div>
              </div>
            </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// WAIVE PLAYER MODAL
// ============================================================================
const WaiveModal = ({ player, teamAbbr, onClose, onConfirm }) => {
  const [useStretch, setUseStretch] = useState(false);

  if (!player) return null;

  const analysis = analyzeWaive(player);
  const yearLabels = ['2025-26', '2026-27', '2027-28', '2028-29', '2029-30', '2030-31', '2031-32'];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="waive-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>&times;</button>

        <div className="waive-modal-header">
          <div className="waive-modal-label">Waive Player</div>
          <div className="waive-modal-name">{player.name}</div>
          <div className="waive-modal-meta">
            {teamAbbr} · {formatSalaryFull(player.salary2526)}/yr
            {player.isTwoWay && ' · Two-Way'}
          </div>
        </div>

        <div className="waive-modal-body">
          {/* Contract Breakdown — show what's guaranteed vs not */}
          <div className="waive-section-title">Contract Guarantee Analysis</div>
          <div className="waive-breakdown">
            {analysis.details.map((d, i) => (
              <div key={i} className="cap-row">
                <span className="cap-row-label">
                  {d.year}
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 8, marginLeft: 6, opacity: 0.6 }}>
                    {d.status}
                  </span>
                </span>
                <span className="cap-row-value" style={{ color: d.dead ? 'var(--accent-red)' : 'var(--accent-green)' }}>
                  {d.dead ? formatSalaryFull(d.amount) : '$0'}
                </span>
              </div>
            ))}
          </div>

          {/* Dead Money Summary */}
          <div className="waive-section-title">
            {useStretch && analysis.stretch ? 'Stretched Dead Money' : 'Dead Money Impact'}
          </div>
          <div className="waive-summary">
            <div className="waive-summary-item">
              <div className="waive-summary-label">Total Dead Money</div>
              <div className={`waive-summary-value ${analysis.totalDead > 0 ? 'red' : 'green'}`}>
                {analysis.totalDead > 0 ? formatSalaryFull(analysis.totalDead) : '$0'}
              </div>
            </div>
            <div className="waive-summary-item">
              <div className="waive-summary-label">
                {useStretch && analysis.stretch ? 'Stretched Over' : 'Dead Cap Years'}
              </div>
              <div className="waive-summary-value">
                {useStretch && analysis.stretch
                  ? `${analysis.stretch.stretchYears} years`
                  : `${analysis.deadYears} year${analysis.deadYears !== 1 ? 's' : ''}`
                }
              </div>
            </div>
            {useStretch && analysis.stretch && (
              <div className="waive-summary-item">
                <div className="waive-summary-label">Year 1 Savings</div>
                <div className="waive-summary-value green">
                  {formatSalaryFull(analysis.stretch.savingsYear1)}
                </div>
              </div>
            )}
          </div>

          {/* Stretched schedule */}
          {useStretch && analysis.stretch && (
            <>
              <div className="waive-section-title">Stretched Cap Hit By Year</div>
              <div className="waive-breakdown">
                {analysis.stretch.schedule.map((amount, i) => (
                  <div key={i} className="cap-row">
                    <span className="cap-row-label">{yearLabels[i] || `Year ${i + 1}`}</span>
                    <span className="cap-row-value" style={{ color: 'var(--accent-red)' }}>
                      {formatSalaryFull(amount)}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Stretch toggle */}
          {analysis.stretch && analysis.deadYears > 0 && (
            <button
              className={`waive-stretch-toggle ${useStretch ? 'active' : ''}`}
              onClick={() => setUseStretch(!useStretch)}
            >
              <span className="waive-stretch-check">{useStretch ? '\u25A0' : '\u25A1'}</span>
              <span>Apply Stretch Provision</span>
              <span className="waive-stretch-hint">
                Spread {formatSalary(analysis.totalDead)} over {analysis.stretch.stretchYears} years ({formatSalary(analysis.stretch.annualHit)}/yr)
              </span>
            </button>
          )}

          {/* CBA context note */}
          <div className="waive-warning">
            {analysis.canWaiveClean
              ? `${player.name} has no guaranteed money remaining. Waiving is free and clear.`
              : `${player.name} will be placed on waivers for 48 hours. If unclaimed, ${formatSalaryFull(analysis.totalDead)} in dead cap will remain on ${teamAbbr}'s books${useStretch && analysis.stretch ? ` (stretched to ${formatSalary(analysis.stretch.annualHit)}/yr)` : ''}.`
            }
            {' '}Player becomes an unrestricted free agent.
          </div>
        </div>

        <div className="waive-modal-actions">
          <button className="waive-cancel-btn" onClick={onClose}>Cancel</button>
          <button className="waive-confirm-btn" onClick={() => onConfirm(player.name, useStretch)}>
            Confirm Waive
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// LAYOUT COMPONENTS
// ============================================================================
const Header = ({ onHome, page, onNav, theme, onToggleTheme, isSeasonMode, seasonPhase, controlTeam, onChangeTeam, children }) => {
  const faDisabled = isSeasonMode && (seasonPhase === 'playoffs' || seasonPhase === 'draft');
  const TEAMS_LIST = Object.entries({ATL:'Hawks',BOS:'Celtics',BKN:'Nets',CHA:'Hornets',CHI:'Bulls',CLE:'Cavaliers',DAL:'Mavericks',DEN:'Nuggets',DET:'Pistons',GSW:'Warriors',HOU:'Rockets',IND:'Pacers',LAC:'Clippers',LAL:'Lakers',MEM:'Grizzlies',MIA:'Heat',MIL:'Bucks',MIN:'Timberwolves',NOP:'Pelicans',NYK:'Knicks',OKC:'Thunder',ORL:'Magic',PHI:'76ers',PHX:'Suns',POR:'Trail Blazers',SAC:'Kings',SAS:'Spurs',TOR:'Raptors',UTA:'Jazz',WAS:'Wizards'}).sort((a,b)=>a[0].localeCompare(b[0]));
  return (
    <header className="header">
      <div className="header-inner">
        <button className="header-brand" onClick={onHome}>
          <img src="/hft_logo.png" alt="HFT" className="header-logo" />
          <div>
            <div className="header-title">Hoops For Thought</div>
            <div className="header-subtitle">GM Simulator</div>
          </div>
        </button>
        <nav className="nav">
          <button className={`nav-btn ${page === 'teams' ? 'active' : ''}`} onClick={() => onNav('teams')}>Teams</button>
          <button className={`nav-btn ${page === 'trade' ? 'active' : ''}`} onClick={() => onNav('trade')}>Trade</button>
          <button className={`nav-btn ${page === 'freeAgency' ? 'active' : ''}`} onClick={() => !faDisabled && onNav('freeAgency')} disabled={faDisabled} style={faDisabled ? { opacity: 0.35, cursor: 'not-allowed' } : {}}>Free Agents</button>
          <button className={`nav-btn ${page === 'draft' ? 'active' : ''}`} onClick={() => onNav('draft')}>Draft</button>
          <button className={`nav-btn ${page === 'search' ? 'active' : ''}`} onClick={() => onNav('search')}>Search</button>
          <button className={`nav-btn ${page === 'history' ? 'active' : ''}`} onClick={() => onNav('history')}>History</button>
          {isSeasonMode && <button className={`nav-btn ${page === 'season' ? 'active' : ''}`} onClick={() => onNav('season')}>Season</button>}
        </nav>
        {children}
        {/* Global team control — sandbox: dropdown, season: locked indicator */}
        {onChangeTeam ? (
          <div className="header-team-picker">
            {controlTeam ? (
              <img src={teamLogoUrl(getTeam(controlTeam)?.nbaId)} alt="" style={{ width: 20, height: 20 }} />
            ) : null}
            <select className="header-team-select" value={controlTeam} onChange={e => onChangeTeam(e.target.value)}>
              {!controlTeam && <option value="">Pick your team</option>}
              {TEAMS_LIST.map(([abbr, name]) => <option key={abbr} value={abbr}>{name}</option>)}
            </select>
          </div>
        ) : isSeasonMode && controlTeam ? (
          <div className="header-team-picker" style={{ cursor: 'default' }}>
            <img src={teamLogoUrl(getTeam(controlTeam)?.nbaId)} alt="" style={{ width: 20, height: 20 }} />
            <span className="header-team-select" style={{ pointerEvents: 'none' }}>{getTeam(controlTeam)?.name}</span>
          </div>
        ) : null}
        <button className="theme-toggle" onClick={onToggleTheme} title={theme === 'dark' ? 'Light mode' : 'Dark mode'}>
          {theme === 'dark' ? '\u2600' : '\u263E'}
        </button>
      </div>
    </header>
  );
};

// ============================================================================
// CONTEXTUAL HELP
// ============================================================================
const HELP_CONTENT = {
  teams: {
    title: 'Team Browser',
    sections: [
      { heading: 'Overview', text: 'Browse all 30 NBA teams sorted by conference and division. Each card shows the team\'s current payroll, cap space, player count, and cap status (under cap, over cap, above first or second apron).' },
      { heading: 'Team Detail', text: 'Click any team to see their full salary breakdown across 4 years, player ratings, contract options, and dead cap. Click any player to see their contract details, extension eligibility, and waive analysis.' },
      { heading: 'Waiving Players', text: 'From the player modal, you can waive a player outright or use the stretch provision to spread dead cap over multiple years. Dead cap entries appear on the roster but cannot be waived again.' },
      { heading: 'Extensions', text: 'Players with extension eligibility show a badge on their row. Open their modal to execute an extension with custom years and salary using the slider or by typing an exact amount.' },
      { heading: 'Trade Machine', text: 'Build trades between 2-6 teams. The system checks salary matching, roster limits, and trade eligibility in real time. In Season Sim, your team is always one side of the trade.' },
    ],
  },
  trade: {
    title: 'Trade Machine',
    sections: [
      { heading: 'Building a Trade', text: 'Select 2 or 3 teams using the team selector tabs. Click players from each team\'s roster to add them to the trade package. The engine validates salary matching in real time.' },
      { heading: 'Salary Matching Rules', text: 'Teams under both aprons: up to 200% + $250K (under $7.25M), flat +$8.527M ($7.25M-$29M), or 125% + $250K (above $29M). First apron teams: 110% only. Second apron teams: dollar-for-dollar, no aggregation allowed.' },
      { heading: 'Hard Caps', text: 'Some teams are hard-capped at the first or second apron due to offseason transactions (sign-and-trades, MLE usage). The engine flags these violations automatically.' },
      { heading: 'Post-Deadline', text: 'During the Post-Deadline phase, all trades are blocked until the offseason.' },
    ],
  },
  freeAgency: {
    title: 'Free Agency',
    sections: [
      { heading: 'Signing Players', text: 'Browse available free agents sorted by overall rating, prior salary, or market interest. Click a player to see competing offers from AI teams, then submit your own offer.' },
      { heading: 'Cap Tools', text: 'The cap ribbon at the top shows your team\'s payroll, available cap space, and signing tools (MLE, BAE, room exception, minimum contracts). These determine which players you can afford.' },
      { heading: 'Team Selection', text: 'In GM Mode, use the team selector in the header to switch which team you\'re signing players for. Each team\'s cap situation updates automatically.' },
      { heading: 'Offseason Flow', text: 'When the season ends, expiring contracts automatically become unrestricted free agents. After the draft, the offseason begins and you can sign players before advancing to the next year.' },
    ],
  },
  draft: {
    title: 'Draft Simulator',
    sections: [
      { heading: 'Start Screen', text: 'See the full draft order and your team\'s picks across both rounds. Choose to sim directly to your pick or go pick-by-pick to watch the whole draft unfold.' },
      { heading: 'Making Your Pick', text: 'When it\'s your turn, browse the available prospects with scouting reports, measurements, and tier ratings. Select a player, let the AI auto-pick for you, or trade the pick.' },
      { heading: 'Two Rounds', text: 'The draft runs 60 picks across 2 rounds. Round 1 picks get guaranteed rookie-scale contracts. Round 2 picks sign minimum deals. Full prospect classes are available for 2026 and 2027, with generated classes for years beyond.' },
      { heading: 'After the Draft', text: 'Once all 60 picks are made, the draft recap shows every selection. Click "Continue to Offseason" to begin free agency.' },
    ],
  },
  season: {
    title: 'Season Simulator',
    sections: [
      { heading: 'Simulating Games', text: 'Use the simulation controls to play through the regular season game by game, week by week, or all at once. Your team\'s games use detailed box scores while other games are quick-simmed.' },
      { heading: 'Standings', text: 'Conference standings update in real time as you sim. The standings determine playoff seeding and draft order.' },
      { heading: 'AI League Trades [BETA]', text: 'When enabled in Season Sim, teams around the league will make moves throughout the season. You may receive trade proposals you can accept or decline. Toggle this on/off in the phase bar. League activity appears in the news feed on the home page.' },
      { heading: 'Counter Offers', text: 'When AI trades are on, other teams will propose trades to you at the trade deadline and offseason. Review what they are offering and accept or decline.' },
      { heading: 'League News', text: 'Click "Show League News" to open a sidebar with article-style reports of every AI trade and major transaction around the league.' },
      { heading: 'Player Progression', text: 'Player ratings evolve between seasons based on real-world trajectory expectations. Young stars develop, prime players hold steady, and veterans transition into new roles.' },
      { heading: 'Playoffs', text: 'After 82 games, the playoff bracket generates automatically. Sim through each round until a champion is crowned.' },
      { heading: 'Export Recap', text: 'After the season, export a shareable recap image showing the champion, your record, and key moves. Click "Export Season Recap" at the bottom.' },
    ],
  },
  search: {
    title: 'Player Search',
    sections: [
      { heading: 'Finding Players', text: 'Type any player\'s name (2+ characters) to search across all 30 teams. Results show each player\'s team, rating, position, salary, and contract status.' },
      { heading: 'Navigation', text: 'Click any search result to jump directly to that player\'s team page where you can see their full contract details.' },
    ],
  },
  history: {
    title: 'Transaction History',
    sections: [
      { heading: 'Tracking Moves', text: 'Every GM action is logged automatically: trades, signings, waivers, draft picks, and contract extensions. The most recent moves appear at the top.' },
      { heading: 'Details', text: 'Click any transaction to expand it and see the full details including salary figures, teams involved, and the season phase when it occurred.' },
    ],
  },
};

const TUTORIAL_STEPS = [
  {
    title: 'Welcome to Hoops For Thought',
    content: 'This tutorial will walk you through every feature. You can exit at any time and return later using the ? button in the bottom right.',
    icon: '🏀',
  },
  {
    title: 'Two Ways to Play',
    content: 'GM Mode lets you explore any team\'s cap sheet, make trades, sign free agents, and run a full offseason. Season Sim picks one team and lets you simulate an entire NBA season from opening night through the Finals, draft, and into the next year.',
    icon: '1',
  },
  {
    title: 'Team Browser',
    content: 'The TEAMS tab shows all 30 NBA teams organized by conference. Each card shows the team payroll, cap space, and record. Click any team to see their full salary breakdown, player contracts, draft picks, and cap projections across 4 years.',
    icon: '2',
  },
  {
    title: 'Player Details',
    content: 'Click any player on a team\'s roster to open their contract modal. From here you can see their full salary schedule, extension eligibility, and option status. If you\'re managing the team, you can waive, stretch, or extend players.',
    icon: '3',
  },
  {
    title: 'Trade Machine',
    content: 'The TRADE tab lets you build trades between 2 or 3 teams. Select teams, then click players to add them to the trade package. The engine validates salary matching in real time using the actual 2025-26 CBA rules. The Trade Finder at the bottom suggests valid trades based on your team.',
    icon: '4',
  },
  {
    title: 'CBA Salary Matching',
    content: 'Trade rules depend on each team\'s payroll tier. Teams under both aprons get the most flexibility (up to 200% + $250K on smaller salaries). First apron teams can only take back 110%. Second apron teams must match dollar-for-dollar with no aggregation. The engine handles all of this automatically.',
    icon: '5',
  },
  {
    title: 'Free Agency',
    content: 'The FREE AGENTS tab shows all available players with competing AI offers. You can sign players using cap space, the mid-level exception ($14.1M), bi-annual exception ($5.1M), or vet minimums depending on your team\'s cap situation.',
    icon: '6',
  },
  {
    title: 'Draft Simulator',
    content: 'The DRAFT tab lets you scout all 60 prospects across two rounds. When it\'s your pick, choose a prospect, let the AI pick, or trade the pick. Round 1 picks get guaranteed rookie-scale contracts. Round 2 picks sign minimums.',
    icon: '7',
  },
  {
    title: 'Season Simulation (Beta)',
    content: 'In Season Sim mode, use the phase bar at the top to advance through the season: Regular Season, Trade Deadline, Playoffs, Draft, and Offseason. Each phase unlocks and locks different actions. After the offseason, you advance to Year 2 with updated contracts.',
    icon: '8',
  },
  {
    title: 'Save System',
    content: 'Your progress is saved automatically in your browser. Use the save button in the top right to manage multiple save files, switch between GM Mode and Season Sim, or start fresh with a new save.',
    icon: '9',
  },
  {
    title: 'Share Your Build',
    content: 'On any team page, click "Share Build" to generate a shareable summary of your roster. Copy it to your clipboard or post directly to X (Twitter) with your team breakdown and a link to hoops4thought.com.',
    icon: '10',
  },
  {
    title: 'AI League Trades [BETA]',
    content: 'In Season Sim mode, enable AI League Trades to have other teams make moves throughout the season. The league stays active around you. Toggle it on when creating a save or from the phase bar. Other teams may also send you trade proposals you can accept or decline.',
    icon: '11',
  },
  {
    title: 'Player Progression',
    content: 'Between seasons, player ratings evolve based on real-world trajectory expectations. Young stars develop, prime players hold steady, and veterans transition. This affects team dynamics as you advance through years.',
    icon: '12',
  },
  {
    title: 'You\'re Ready',
    content: 'That\'s everything you need to get started. The ? button in the bottom right always shows help for whatever page you\'re on. Have fun building your franchise.',
    icon: '🏀',
  },
];

const ContextualHelp = ({ page, onClose, startTutorial = false }) => {
  const [mode, setMode] = useState(startTutorial ? 'tutorial' : 'help');
  const [step, setStep] = useState(0);
  const help = HELP_CONTENT[page] || HELP_CONTENT.teams;
  const totalSteps = TUTORIAL_STEPS.length;
  const currentStep = TUTORIAL_STEPS[step];

  if (mode === 'tutorial') {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="tutorial-modal" onClick={e => e.stopPropagation()}>
          <button className="modal-close" onClick={onClose}>&times;</button>

          <div className="tutorial-progress">
            <div className="tutorial-progress-bar" style={{ width: `${((step + 1) / totalSteps) * 100}%` }} />
          </div>

          <div className="tutorial-step-counter">
            {step + 1} of {totalSteps}
          </div>

          <div className="tutorial-icon">{currentStep.icon}</div>
          <div className="tutorial-title">{currentStep.title}</div>
          <div className="tutorial-content">{currentStep.content}</div>

          <div className="tutorial-nav">
            {step > 0 ? (
              <button className="tutorial-nav-btn" onClick={() => setStep(step - 1)}>Back</button>
            ) : (
              <button className="tutorial-nav-btn" onClick={() => setMode('help')}>Page Help</button>
            )}
            {step < totalSteps - 1 ? (
              <button className="tutorial-nav-btn primary" onClick={() => setStep(step + 1)}>Next</button>
            ) : (
              <button className="tutorial-nav-btn primary" onClick={onClose}>Get Started</button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="help-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>&times;</button>
        <div className="help-modal-header">
          <div className="help-modal-label">How It Works</div>
          <div className="help-modal-title">{help.title}</div>
        </div>
        <div className="help-modal-body">
          {help.sections.map((s, i) => (
            <div key={i} className="help-section">
              <div className="help-section-heading">{s.heading}</div>
              <div className="help-section-text">{s.text}</div>
            </div>
          ))}
        </div>
        <div className="help-modal-footer">
          <button className="tutorial-start-btn" onClick={() => { setMode('tutorial'); setStep(0); }}>
            Full App Tutorial
          </button>
        </div>
      </div>
    </div>
  );
};

const Footer = () => (
  <footer className="footer">
    <div className="footer-brand">
      <img src="/hft_logo.png" alt="" className="footer-logo-img" />
      <div className="footer-title">Hoops For Thought</div>
    </div>
    <div className="footer-sub">NBA GM Simulator &middot; 2025-26 Season &middot; Beta</div>
    <div className="footer-links">
      <a href="https://twitter.com/Hoops4Thought" target="_blank" rel="noopener noreferrer" className="footer-link">@Hoops4Thought</a>
      <span className="footer-link-sep">&middot;</span>
      <a href="mailto:Contact@Hoops4thought.com" className="footer-link">Contact@Hoops4thought.com</a>
    </div>
    <div className="footer-disclaimer">Hoops For Thought is an independent fan project. Not affiliated with or endorsed by the NBA or any team. All salary data and roster information is for simulation purposes only.</div>
    <div className="footer-copy">&copy; {new Date().getFullYear()} Hoops For Thought. All rights reserved.</div>
  </footer>
);

// Season phases in order
const SEASON_PHASES = [
  { key: 'regular', label: 'Regular Season', short: 'Season', desc: 'Make trades, sign free agents, manage rosters. Salary matching rules enforced.', guide: 'Use the Trade Machine and Free Agency board. Set your Depth Chart before advancing.' },
  { key: 'postDeadline', label: 'Post-Deadline', short: 'Deadline', desc: 'Trade deadline has passed (Feb 6). No trades until the offseason.', guide: 'Review your roster and depth chart. Free agent signings still available.' },
  { key: 'playoffs', label: 'Playoffs', short: 'Playoffs', desc: 'Playoff bracket and results. Rosters are locked. No free agent signings.', guide: 'View the Season Hub to see playoff results and standings.' },
  { key: 'draft', label: 'NBA Draft', short: 'Draft', desc: 'Select prospects or trade your picks. Rookies join rosters on scale contracts.', guide: 'Head to the Draft board to make your picks or trade them.' },
  { key: 'offseason', label: 'Offseason', short: 'Offseason', desc: 'Sign free agents, extend players, build your roster for the next season.', guide: 'Visit Free Agency to sign players, then Advance Season when ready.' },
];

const PHASE_WARNINGS = {
  postDeadline: {
    title: 'Advance Past Trade Deadline?',
    warning: 'The trade deadline will pass. You will not be able to make any trades until the offseason.',
    note: 'Free agent signings will still be available. Rookie scale and veteran extension deadlines have already passed.',
    color: 'var(--accent-gold)',
  },
  playoffs: {
    title: 'Begin Playoffs?',
    warning: 'All roster moves will be frozen during the playoffs. No trades, no free agent signings, no extensions.',
    note: 'Make sure your roster is finalized before advancing.',
    color: 'var(--accent-red)',
  },
  draft: {
    title: 'Begin the Draft?',
    warning: 'You\'ll enter the draft where you select prospects or trade your picks. After the draft, the offseason free agency period begins.',
    note: 'You\'ll be taken to the draft board automatically.',
    color: '#7B2FF7',
  },
  offseason: {
    title: 'Enter the Offseason?',
    warning: 'All expiring contracts will become free agents. You\'ll be able to sign players from the open market and extend current players.',
    note: 'Advance to the next season when you\'re done building your roster.',
    color: 'var(--accent-green)',
  },
};

const SeasonPhaseBar = ({ phase, mode, onAdvance, onRevert, seasonYear, myTeamExtensions, aiTradesEnabled, onToggleAITrades, aiTradeLog }) => {
  const [showInfo, setShowInfo] = useState(false);
  const [showRevertConfirm, setShowRevertConfirm] = useState(false);
  const [showAdvanceConfirm, setShowAdvanceConfirm] = useState(false);
  const [tickerDismissed, setTickerDismissed] = useState(false);
  const [lastSeenTradeCount, setLastSeenTradeCount] = useState(0);
  
  // Re-show ticker when new trades appear
  const currentTradeCount = aiTradeLog?.length || 0;
  useEffect(() => {
    if (currentTradeCount > lastSeenTradeCount && lastSeenTradeCount > 0) {
      setTickerDismissed(false);
    }
  }, [currentTradeCount, lastSeenTradeCount]);
  const currentIdx = SEASON_PHASES.findIndex(p => p.key === phase);
  const current = SEASON_PHASES[currentIdx] || SEASON_PHASES[0];
  const canAdvance = currentIdx < SEASON_PHASES.length - 1;
  const canRevert = currentIdx > 0;
  const nextPhase = canAdvance ? SEASON_PHASES[currentIdx + 1] : null;
  const isSandbox = mode === 'sandbox';
  const warning = nextPhase ? PHASE_WARNINGS[nextPhase.key] : null;

  const handleAdvance = () => {
    if (isSandbox && !warning) { onAdvance(); }
    else { setShowAdvanceConfirm(true); }
  };

  const handleRevert = () => {
    if (isSandbox) { onRevert(); }
    else { setShowRevertConfirm(true); }
  };

  const yearLabel = seasonYear ? `${seasonYear}-${String(seasonYear + 1).slice(2)}` : '2025-26';

  return (
    <div className="phase-bar">
      <div className="phase-bar-inner">
        {/* Steps */}
        <div className="phase-steps">
          {SEASON_PHASES.map((p, i) => (
            <div key={p.key} className={`phase-step ${i < currentIdx ? 'completed' : ''} ${i === currentIdx ? 'active' : ''} ${i > currentIdx ? 'upcoming' : ''}`}>
              <div className="phase-step-dot" />
              <span className="phase-step-label">{p.short}</span>
              {i < SEASON_PHASES.length - 1 && <div className="phase-step-line" />}
            </div>
          ))}
        </div>

        {/* Phase guidance */}
        {current.guide && (
          <div style={{ fontSize: 10, color: 'var(--text-dim)', textAlign: 'center', padding: '3px 0', fontStyle: 'italic' }}>{current.guide}</div>
        )}

        {/* Controls */}
        <div className="phase-controls">
          <span className="phase-mode-badge">{isSandbox ? 'GM Mode' : yearLabel}</span>
          {!isSandbox && (
          <button
            className={`phase-toggle-btn ${aiTradesEnabled ? 'active' : ''}`}
            onClick={onToggleAITrades}
            title={aiTradesEnabled ? 'AI trades enabled — click to disable' : 'AI trades disabled — click to enable'}
          >
            AI Trades {aiTradesEnabled ? 'ON' : 'OFF'} <span className="beta-tag" style={{fontSize:7,marginLeft:3}}>BETA</span>
          </button>
          )}
          <button className="phase-info-btn" onClick={() => setShowInfo(!showInfo)} title="How phases work">
            <span>i</span>
          </button>
          {canRevert && (
            <button className="phase-revert-btn" onClick={handleRevert}>
              &larr; Back
            </button>
          )}
          {canAdvance && (
            <button className="phase-advance-btn" onClick={handleAdvance}>
              {nextPhase.short} &rarr;
            </button>
          )}
        </div>
      </div>

      {/* Info Popover */}
      {showInfo && (
        <div className="phase-info-popover">
          <div className="phase-info-close" onClick={() => setShowInfo(false)}>&times;</div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 8, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 8 }}>Season Phases</div>
          {SEASON_PHASES.map((p, i) => (
            <div key={p.key} className={`phase-info-row ${p.key === phase ? 'current' : ''}`}>
              <span className="phase-info-num">{i + 1}</span>
              <div>
                <div style={{ fontWeight: 600, fontSize: 12 }}>{p.label}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>{p.desc}</div>
              </div>
            </div>
          ))}
          <div style={{ fontSize: 10, color: 'var(--text-dim)', marginTop: 8, borderTop: '1px solid var(--border)', paddingTop: 6 }}>
            Advance through phases in order. Reverting will undo any progress made in the current phase.
          </div>
        </div>
      )}

      {/* Phase Transition Popup */}
      {showAdvanceConfirm && nextPhase && (
        <div className="modal-overlay" onClick={() => setShowAdvanceConfirm(false)}>
          <div className="phase-transition-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowAdvanceConfirm(false)}>&times;</button>
            {warning && <div className="phase-transition-accent" style={{ background: warning.color }} />}
            <div className="phase-transition-body">
              <div className="phase-transition-label">Phase Transition</div>
              <div className="phase-transition-title">{warning?.title || `Advance to ${nextPhase.label}?`}</div>
              <div className="phase-transition-warning">{warning?.warning || nextPhase.desc}</div>
              {warning?.note && <div className="phase-transition-note">{warning.note}</div>}
              {/* Extension eligibility warnings */}
              {myTeamExtensions && myTeamExtensions.players.length > 0 && (() => {
                let relevantPlayers = [];
                if (nextPhase.key === 'postDeadline') {
                  relevantPlayers = myTeamExtensions.players.filter(p => p.type === 'rookie' || p.type === 'finalYear');
                } else if (nextPhase.key === 'playoffs') {
                  relevantPlayers = myTeamExtensions.players.filter(p => p.type === 'expiring');
                } else if (nextPhase.key === 'draft') {
                  relevantPlayers = myTeamExtensions.players.filter(p => p.type === 'expiring');
                }
                if (relevantPlayers.length === 0) return null;
                return (
                  <div className="phase-transition-ext-warning">
                    <div className="phase-transition-ext-label">Extension Windows Closing</div>
                    <div className="phase-transition-ext-desc">
                      The following players on your roster will lose extension eligibility:
                    </div>
                    <div className="phase-transition-ext-list">
                      {relevantPlayers.map(p => (
                        <div key={p.name} className="phase-transition-ext-player">
                          <span>{p.name}</span>
                          <span className="ext-badge" style={{ borderColor: p.type === 'rookie' ? '#7B2FF7' : p.type === 'expiring' ? 'var(--accent-gold)' : 'var(--accent-green)', color: p.type === 'rookie' ? '#7B2FF7' : p.type === 'expiring' ? 'var(--accent-gold)' : 'var(--accent-green)' }}>{p.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
            <div className="phase-transition-actions">
              <button className="waive-cancel-btn" onClick={() => setShowAdvanceConfirm(false)}>Cancel</button>
              <button
                className="phase-advance-btn"
                style={warning ? { background: warning.color, borderColor: warning.color } : {}}
                onClick={() => { onAdvance(); setShowAdvanceConfirm(false); }}
              >
                Confirm &rarr;
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Revert Confirmation */}
      {showRevertConfirm && (
        <div className="modal-overlay" onClick={() => setShowRevertConfirm(false)}>
          <div className="phase-transition-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowRevertConfirm(false)}>&times;</button>
            <div className="phase-transition-accent" style={{ background: 'var(--accent-red)' }} />
            <div className="phase-transition-body">
              <div className="phase-transition-label">Revert Phase</div>
              <div className="phase-transition-title">Go back to {SEASON_PHASES[currentIdx - 1]?.label}?</div>
              <div className="phase-transition-warning">
                This will undo your progress in the current phase. Any actions taken during {current.label} will not be reversed, but the phase will move back.
              </div>
            </div>
            <div className="phase-transition-actions">
              <button className="waive-cancel-btn" onClick={() => setShowRevertConfirm(false)}>Cancel</button>
              <button className="phase-advance-btn" style={{ background: 'var(--accent-red)', borderColor: 'var(--accent-red)' }} onClick={() => { onRevert(); setShowRevertConfirm(false); }}>
                Revert
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Trade Ticker — dismissable */}
      {aiTradeLog && aiTradeLog.length > 0 && !tickerDismissed && (
        <div className="ai-trade-ticker">
          <span className="ai-trade-ticker-label">LEAGUE TRADES</span>
          <div className="ai-trade-ticker-scroll">
            {aiTradeLog.slice(-5).reverse().map((t, i) => (
              <span key={i} className={`ai-trade-ticker-item ${t.type === 'blockbuster' ? 'blockbuster' : ''}`}>
                {t.type === 'blockbuster' && '[BLOCKBUSTER] '}{t.team1} &harr; {t.team2}: {t.team1Sends.map(p => p.name).join(', ')} for {t.team2Sends.map(p => p.name).join(', ')}
              </span>
            ))}
          </div>
          <button className="ai-trade-ticker-close" onClick={() => { setTickerDismissed(true); setLastSeenTradeCount(currentTradeCount); }} title="Dismiss">&times;</button>
        </div>
      )}
    </div>
  );
};

const Loading = ({ message }) => (
  <div className="loading">
    <div className="loading-spinner" />
    <div className="loading-text">{message || 'Loading...'}</div>
  </div>
);

// ============================================================================
// TEAM BROWSER
// ============================================================================
const TeamBrowser = ({ onSelectTeam, standings, salaryData: currentSalaryData }) => {
  const sortTeams = (teams) => [...teams].sort((a, b) => {
    const rA = standings[a.abbr]?.confRank || 99, rB = standings[b.abbr]?.confRank || 99;
    return rA !== rB ? rA - rB : a.name.localeCompare(b.name);
  });

  return (
    <div className="fade-in">
      <div className="section-header">
        <div className="section-label">2025-26 Season</div>
        <div className="section-title">Select a Team</div>
        <div className="section-desc">Choose a franchise to manage. View rosters, contracts, and cap situations.</div>
      </div>
      <div className="conference-header">Eastern Conference</div>
      <div className="team-grid">
        {sortTeams(TEAMS.filter(t => t.conference === 'East')).map(team => {
          const r = standings[team.abbr];
          return (
            <div key={team.abbr} className="team-card" onClick={() => onSelectTeam(team.abbr)}>
              <img src={teamLogoUrl(team.nbaId)} alt={team.abbr} className="team-card-logo" />
              <div className="team-card-info">
                <div className="team-card-name">{team.name}</div>
                <div className="team-card-meta">{team.division} Division</div>
              </div>
              {r && <div className="team-card-record">{r.wins}-{r.losses}</div>}
            </div>
          );
        })}
      </div>
      <div className="conference-header">Western Conference</div>
      <div className="team-grid">
        {sortTeams(TEAMS.filter(t => t.conference === 'West')).map(team => {
          const r = standings[team.abbr];
          return (
            <div key={team.abbr} className="team-card" onClick={() => onSelectTeam(team.abbr)}>
              <img src={teamLogoUrl(team.nbaId)} alt={team.abbr} className="team-card-logo" />
              <div className="team-card-info">
                <div className="team-card-name">{team.name}</div>
                <div className="team-card-meta">{team.division} Division</div>
              </div>
              {r && <div className="team-card-record">{r.wins}-{r.losses}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ============================================================================
// EXTENSION ELIGIBILITY
// ============================================================================
// Rookie Scale: option contains "Q" → on rookie contract, can be extended
// Entering Final Year: salary2627 > 0 but salary2728 === 0, no Q → last guaranteed year next season
// Expiring Veteran: salary2526 > 0, salary2627 === 0 → last year, can extend until June 30
function getExtensionStatus(player) {
  if (!player || player.isDead || player.isTwoWay) return null;
  const opt = player.option || '';
  const hasQ = opt.includes('Q');
  const isExpiring = player.salary2526 > 0 && !player.salary2627;
  const enteringFinalYear = player.salary2627 > 0 && !player.salary2728 && !hasQ;

  if (hasQ) {
    return {
      type: 'rookie',
      label: 'ROOKIE EXT',
      color: '#7B2FF7',
      desc: 'Eligible for a rookie scale extension.',
      deadline: 'Deadline: Before the regular season begins (Oct 20).',
    };
  }
  if (isExpiring) {
    return {
      type: 'expiring',
      label: 'EXPIRING',
      color: 'var(--accent-gold)',
      desc: 'In the final year of their contract \u2014 eligible for a veteran extension.',
      deadline: 'Deadline: June 30 of the current league year.',
    };
  }
  if (enteringFinalYear) {
    return {
      type: 'finalYear',
      label: 'EXT ELIGIBLE',
      color: 'var(--accent-green)',
      desc: 'Entering the final guaranteed year next season \u2014 eligible for a veteran extension.',
      deadline: 'Deadline: Before the regular season begins.',
    };
  }
  return null;
}

// ============================================================================
// SORTABLE ROSTER TABLE
// ============================================================================
const SortableRoster = ({ roster, hasSalary, onSelectPlayer }) => {
  const [sortKey, setSortKey] = useState('ovr');
  const [sortDir, setSortDir] = useState('desc');

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir(key === 'name' ? 'asc' : 'desc');
    }
  };

  const sorted = [...roster].sort((a, b) => {
    let av, bv;
    switch (sortKey) {
      case 'name': av = a.name; bv = b.name; break;
      case 'jerseyNumber': av = parseInt(a.jerseyNumber) || 999; bv = parseInt(b.jerseyNumber) || 999; break;
      case 'salary2526': av = a.salary2526 || 0; bv = b.salary2526 || 0; break;
      case 'salary2627': av = a.salary2627 || 0; bv = b.salary2627 || 0; break;
      case 'position': av = a.position; bv = b.position; break;
      case 'ovr': av = getPlayerRating(a.name); bv = getPlayerRating(b.name); break;
      default: av = 0; bv = 0;
    }
    if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    return sortDir === 'asc' ? av - bv : bv - av;
  });

  const SortTh = ({ label, sKey, className }) => (
    <th className={`sortable-th ${className || ''} ${sortKey === sKey ? 'active' : ''}`} onClick={() => handleSort(sKey)}>
      {label}
      {sortKey === sKey && <span className="sort-arrow">{sortDir === 'asc' ? ' \u25B2' : ' \u25BC'}</span>}
    </th>
  );

  return (
    <table className="roster-table">
      <thead>
        <tr>
          <SortTh label="#" sKey="jerseyNumber" className="" />
          <SortTh label="Player" sKey="name" />
          <SortTh label="Rating" sKey="ovr" className="right" />
          <SortTh label="Pos" sKey="position" />
          {hasSalary && <SortTh label="2025-26" sKey="salary2526" className="right" />}
          {hasSalary && <SortTh label="2026-27" sKey="salary2627" className="right" />}
          {hasSalary && <th className="right">Option</th>}
          {!hasSalary && <th>Height</th>}
          {!hasSalary && <th>College</th>}
        </tr>
      </thead>
      <tbody>
        {sorted.map((player, i) => {
          const ovr = player.ovr || getPlayerRating(player.name);
          const rColor = getRatingColor(ovr);
          return (
          <tr key={player.name} onClick={() => !player.isDead && onSelectPlayer(player)} className={player.isDead ? 'dead-money-row' : ''} style={{ cursor: player.isDead ? 'default' : 'pointer' }}>
            <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-muted)', width: 36 }}>
              {player.isDead ? '' : player.jerseyNumber}
            </td>
            <td>
              <div className="player-cell" style={{ position: 'relative' }}>
                {!player.isDead && <PlayerHeadshot name={player.name} nbaComId={player.nbaComId} />}
                <div>
                  <div className="player-name">
                    {player.name}
                    {player.isTwoWay && <span className="badge badge-muted" style={{ marginLeft: 6, fontSize: 8 }}>2-WAY</span>}
                    {player.isDead && <span className="dead-money-badge">DEAD CAP</span>}
                    {(() => {
                      const ext = getExtensionStatus(player);
                      if (!ext) return null;
                      return <span className="ext-badge" style={{ borderColor: ext.color, color: ext.color }}>{ext.label}</span>;
                    })()}
                  </div>
                </div>
              </div>
            </td>
            <td className="right">
              {player.isDead ? <span style={{ color: 'var(--text-dim)' }}>--</span> : <span className="ovr-badge" style={{ borderLeftColor: rColor }}>{ovr}</span>}
            </td>
            <td>{player.isDead ? '' : <span className="badge badge-muted">{player.position}</span>}</td>
            {hasSalary && <td className="right"><span className="salary">{formatSalary(player.salary2526)}</span></td>}
            {hasSalary && (
              <td className="right">
                <span className="salary" style={{ color: player.salary2627 ? 'var(--text)' : 'var(--accent-red)' }}>
                  {player.salary2627 ? formatSalary(player.salary2627) : 'UFA'}
                </span>
              </td>
            )}
            {hasSalary && <td className="right"><span className="contract-years">{player.option || '\u2014'}</span></td>}
            {!hasSalary && <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{player.height}</td>}
            {!hasSalary && <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)' }}>{player.college}</td>}
          </tr>
          );
        })}
      </tbody>
    </table>
  );
};

// ============================================================================
// TEAM DETAIL
// ============================================================================
const TeamDetail = ({ teamAbbr, onBack, standings, salaryData: currentSalaryData, onWaivePlayer, onExtendPlayer, tradeHistory = [], onOpenDepthChart }) => {
  const team = getTeam(teamAbbr);
  const { roster, loading, error } = useRoster(teamAbbr, currentSalaryData);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [waiveTarget, setWaiveTarget] = useState(null);
  const [showShareBuild, setShowShareBuild] = useState(false);
  const record = standings[teamAbbr];
  const salaryTeam = currentSalaryData?.[teamAbbr] || SALARY_DATA[teamAbbr];
  const hasSalary = !!salaryTeam;

  if (!team) return <div>Team not found.</div>;
  if (loading) return <Loading message={`Loading ${team.name} roster...`} />;
  if (error) return (
    <div className="fade-in">
      <button className="back-btn" onClick={onBack}>&larr; Back to all teams</button>
      <div style={{ padding: '60px 0', textAlign: 'center' }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 24, color: 'var(--accent-red)' }}>Error</div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 8 }}>{error}</div>
      </div>
    </div>
  );

  const totalSalary = hasSalary ? salaryTeam.payroll : 0;
  const capSpace = CBA.salaryCap - totalSalary;
  const overCap = totalSalary > CBA.salaryCap;
  const inTax = totalSalary > CBA.luxuryTax;
  const taxAmount = inTax ? totalSalary - CBA.luxuryTax : 0;
  const aboveFirstApron = totalSalary > CBA.firstApron;
  const aboveSecondApron = totalSalary > CBA.secondApron;

  return (
    <div className="fade-in">
      <button className="back-btn" onClick={onBack}>&larr; Back to all teams</button>

      <div className="team-hero">
        <img src={teamLogoUrl(team.nbaId)} alt={team.abbr} className="team-hero-logo" />
        <div className="team-hero-info">
          <div className="team-hero-name">{team.name}</div>
          <div className="team-hero-meta">
            {team.conference}ern Conference &middot; {team.division} Division
            {record ? ` \u00B7 ${record.wins}-${record.losses}` : ''}
          </div>
        </div>
        {hasSalary && (
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="share-build-btn" onClick={() => onOpenDepthChart && onOpenDepthChart(teamAbbr)} title="Set starting lineup and minutes">
              Depth Chart
            </button>
            <button className="share-build-btn" onClick={() => setShowShareBuild(true)} title="Share your roster build">
              Share Build
            </button>
          </div>
        )}
      </div>

      {hasSalary ? (
        <>
          <div className="stat-ribbon stat-ribbon-5">
            <div className="stat-item">
              <div className="stat-label">Team Payroll</div>
              <div className="stat-value">{formatSalary(totalSalary)}</div>
            </div>
            <div className="stat-item">
              <div className="stat-label">Cap Space</div>
              <div className={`stat-value ${overCap ? 'red' : 'green'}`}>
                {overCap ? '-' : ''}{formatSalary(Math.abs(capSpace))}
              </div>
            </div>
            <div className="stat-item">
              <div className="stat-label">Tax Status</div>
              <div className={`stat-value ${inTax ? 'red' : 'green'}`} style={{ fontSize: inTax ? 16 : 22 }}>
                {inTax ? `In Tax (+${formatSalary(taxAmount)})` : 'Under Tax'}
              </div>
            </div>
            <div className="stat-item">
              <div className="stat-label">Apron</div>
              <div className={`stat-value ${aboveSecondApron ? 'red' : aboveFirstApron ? 'gold' : 'green'}`} style={{ fontSize: 16 }}>
                {aboveSecondApron ? '2nd Apron' : aboveFirstApron ? '1st Apron' : 'Below Aprons'}
              </div>
            </div>
            <div className="stat-item">
              <div className="stat-label">Roster</div>
              <div className="stat-value">{roster.length}</div>
            </div>
          </div>

          <div style={{ padding: '20px 0' }}>
            <div className="section-label" style={{ marginBottom: 12 }}>Salary Cap Breakdown</div>
            <div className="cap-table">
              {[
                ['Salary Cap', formatSalaryFull(CBA.salaryCap), ''],
                ['Team Payroll', formatSalaryFull(totalSalary), totalSalary > CBA.salaryCap ? 'red' : ''],
                ['Cap Space', (overCap ? '-' : '') + formatSalaryFull(Math.abs(capSpace)), overCap ? 'red' : 'green'],
                ['Luxury Tax Line', formatSalaryFull(CBA.luxuryTax), ''],
                ['Tax Amount', inTax ? formatSalaryFull(taxAmount) : '$0', inTax ? 'red' : ''],
                ['First Apron', formatSalaryFull(CBA.firstApron), ''],
                ['Second Apron', formatSalaryFull(CBA.secondApron), ''],
              ].map(([label, value, color], i) => (
                <div key={i} className="cap-row">
                  <span className="cap-row-label">{label}</span>
                  <span className="cap-row-value" style={color ? { color: `var(--accent-${color})` } : {}}>{value}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : (
        <div className="stat-ribbon stat-ribbon-4">
          <div className="stat-item"><div className="stat-label">Roster</div><div className="stat-value">{roster.length}</div></div>
          <div className="stat-item"><div className="stat-label">Record</div><div className="stat-value">{record ? `${record.wins}-${record.losses}` : '\u2014'}</div></div>
          <div className="stat-item"><div className="stat-label">Conf. Rank</div><div className="stat-value">{record?.confRank ? `#${record.confRank}` : '\u2014'}</div></div>
          <div className="stat-item"><div className="stat-label">Salary Data</div><div className="stat-value" style={{ fontSize: 14, color: 'var(--text-muted)' }}>Pending</div></div>
        </div>
      )}

      {/* Cap Projection Chart — Collapsible */}
      {hasSalary && (() => {
        const years = [
          { label: '25-26', key: 'salary2526' },
          { label: '26-27', key: 'salary2627' },
          { label: '27-28', key: 'salary2728' },
          { label: '28-29', key: 'salary2829' },
        ];
        const projections = years.map(yr => {
          let total = 0;
          let guaranteed = 0;
          let nonGuaranteed = 0;
          let deadCap = 0;
          let playerCount = 0;
          const playerBreakdown = [];
          for (const p of (salaryTeam.players || [])) {
            const val = p[yr.key] || 0;
            if (val <= 0) continue;
            if (p.isDead) { deadCap += val; }
            else {
              total += val;
              const hasTeamOpt = p.option && (p.option.includes('T') && p.option.includes(yr.label.replace('-', '-')));
              if (hasTeamOpt) nonGuaranteed += val;
              else guaranteed += val;
              playerCount++;
              playerBreakdown.push({ name: p.name, salary: val });
            }
          }
          // Sort player breakdown by salary descending
          playerBreakdown.sort((a, b) => b.salary - a.salary);
          return { ...yr, total: total + deadCap, guaranteed, nonGuaranteed, deadCap, playerCount, playerBreakdown };
        });
        const maxVal = Math.max(...projections.map(p => p.total), CBA.secondApron);
        const capThresholds = [
          { val: CBA.salaryCap, label: 'Cap', color: 'var(--accent-green)' },
          { val: CBA.luxuryTax, label: 'Tax', color: 'var(--accent-gold)' },
          { val: CBA.firstApron, label: '1st', color: 'var(--accent-red)' },
          { val: CBA.secondApron, label: '2nd', color: '#c0392b' },
        ];
        const chartHeight = 200;

        return (
          <details className="cap-projections-toggle" style={{ marginTop: 24 }}>
            <summary className="cap-projections-summary">
              <span className="cap-projections-summary-title">Cap Projections</span>
              <span className="cap-projections-summary-preview">
                {projections.map(yr => `'${yr.label.split('-')[0]}: ${formatSalary(yr.total)}`).join('  ')}
              </span>
            </summary>
            <div className="cap-chart-container">
              <div className="cap-chart">
                {capThresholds.map(t => {
                  const pct = (t.val / maxVal) * 100;
                  return (
                    <div key={t.label} className="cap-chart-line" style={{ bottom: `${pct}%` }}>
                      <span className="cap-chart-line-label" style={{ color: t.color }}>{t.label} {formatSalary(t.val)}</span>
                    </div>
                  );
                })}
                <div className="cap-chart-bars">
                  {projections.map((yr, i) => {
                    const pctTotal = (yr.total / maxVal) * 100;
                    // Player color palette (warm, muted, distinguishable)
                    const PLAYER_COLORS = [
                      '#5b8c7a','#8b6f47','#6b7f99','#9b6b6b','#7a6b8f','#8f8b5b','#5b7a8c','#8c5b6f',
                      '#6f8b5b','#5b6f8c','#8c7a5b','#6b8c8f','#8f5b7a','#7a8c5b','#5b8c6f','#8b5b8f',
                    ];
                    return (
                      <div key={i} className="cap-chart-bar-group">
                        <div className="cap-chart-bar-wrapper" style={{ height: chartHeight }}>
                          <div className="cap-chart-bar" style={{ height: `${pctTotal}%`, background: 'transparent', display: 'flex', flexDirection: 'column-reverse', overflow: 'hidden' }}>
                            {/* Dead cap segment at top */}
                            {yr.deadCap > 0 && (
                              <div title="Dead Cap" style={{ height: `${(yr.deadCap / yr.total) * 100}%`, background: 'repeating-linear-gradient(45deg, var(--text-dim), var(--text-dim) 2px, transparent 2px, transparent 4px)', opacity: 0.4, flexShrink: 0 }} />
                            )}
                            {/* Player segments */}
                            {yr.playerBreakdown.map((pb, pi) => (
                              <div
                                key={pi}
                                title={`${pb.name}: ${formatSalary(pb.salary)}`}
                                style={{
                                  height: `${(pb.salary / yr.total) * 100}%`,
                                  background: PLAYER_COLORS[pi % PLAYER_COLORS.length],
                                  borderBottom: pi < yr.playerBreakdown.length - 1 ? '1px solid rgba(255,255,255,0.2)' : 'none',
                                  flexShrink: 0,
                                  cursor: 'default',
                                  transition: 'opacity 0.15s',
                                  minHeight: 1,
                                }}
                                onMouseEnter={e => { e.currentTarget.style.opacity = '0.75'; }}
                                onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
                              />
                            ))}
                          </div>
                        </div>
                        <div className="cap-chart-bar-label">{yr.label}</div>
                        <div className="cap-chart-bar-value">{formatSalary(yr.total)}</div>
                        <div className="cap-chart-bar-count">{yr.playerCount} players</div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="cap-chart-legend">
                <span className="cap-chart-legend-item"><span className="cap-chart-dot" style={{ background: 'repeating-linear-gradient(45deg, var(--text-dim), var(--text-dim) 2px, transparent 2px, transparent 4px)', opacity: 0.4 }} /> Dead Cap</span>
                <span className="cap-chart-legend-item" style={{ fontSize: 9, color: 'var(--text-muted)' }}>Hover bars to see player salary</span>
              </div>
            </div>
          </details>
        );
      })()}

      <div className="roster-section">
        <div className="roster-section-title">Active Roster \u2014 {roster.filter(p => !p.isTwoWay && !p.isDead).length} Standard + {roster.filter(p => p.isTwoWay).length} Two-Way{roster.some(p => p.isDead) && ` + ${roster.filter(p => p.isDead).length} Dead Cap`}</div>
        <SortableRoster roster={roster} hasSalary={hasSalary} onSelectPlayer={setSelectedPlayer} />
      </div>

      {/* Draft Capital */}
      {(() => {
        const teamPickData = DRAFT_PICKS[teamAbbr];
        if (!teamPickData) return null;
        const basePicks = JSON.parse(JSON.stringify(teamPickData.picks));
        const tpes = teamPickData.tradeExceptions || [];
        const years = [2026, 2027, 2028, 2029, 2030, 2031];

        // Overlay user trades onto picks
        const userTradedOut = []; // picks this team sent away
        const userTradedIn = [];  // picks this team received
        for (const trade of (tradeHistory || [])) {
          if (!trade.picks) continue;
          const teams = trade.teams || [];
          const pDests = trade.pickDestinations || {};
          for (const [sender, sentPicks] of Object.entries(trade.picks)) {
            for (const pick of (sentPicks || [])) {
              if (!pick.year || !pick.round) continue;
              // Determine receiver using pickDestinations for 3+ team trades
              let receiver;
              if (teams.length === 2) {
                receiver = teams.find(t => t !== sender) || '';
              } else {
                const senderDests = pDests[sender] || {};
                receiver = senderDests[pick.id] || Object.values(senderDests)[0] || teams.find(t => t !== sender) || '';
              }
              if (sender === teamAbbr) {
                userTradedOut.push({ ...pick, to: receiver });
              } else if (receiver === teamAbbr) {
                userTradedIn.push({ ...pick, from: sender });
              }
            }
          }
        }

        // Apply: mark sent picks as traded, add received picks
        for (const out of userTradedOut) {
          // Find matching own/incoming pick and mark traded
          const idx = basePicks.findIndex(p =>
            p.year === out.year && p.round === out.round &&
            (p.status === 'own' || p.status === 'incoming')
          );
          if (idx !== -1) {
            basePicks[idx] = { ...basePicks[idx], status: 'traded', to: out.to, note: 'User trade' };
          } else {
            basePicks.push({ year: out.year, round: out.round, status: 'traded', to: out.to, note: 'User trade' });
          }
        }
        for (const inc of userTradedIn) {
          basePicks.push({ year: inc.year, round: inc.round, status: 'incoming', from: inc.from, note: 'Acquired via trade' });
        }

        const picks = basePicks;

        return (
          <div className="roster-section" style={{ marginTop: 24 }}>
            <div className="roster-section-title">Draft Capital</div>
            <table className="roster-table pick-table">
              <thead>
                <tr>
                  <th>Year</th>
                  <th>Pick</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {years.map(yr => {
                  const yearPicks = picks.filter(p => p.year === yr);
                  if (yearPicks.length === 0) {
                    return (
                      <tr key={yr}>
                        <td className="pick-year">{yr}</td>
                        <td>Own 1st + Own 2nd</td>
                        <td><span className="badge badge-muted">OWN</span></td>
                      </tr>
                    );
                  }
                  return yearPicks.map((p, i) => (
                    <tr key={`${yr}-${p.round}-${i}`}>
                      {i === 0 && <td className="pick-year" rowSpan={yearPicks.length}>{yr}</td>}
                      <td>
                        <span style={{ fontWeight: p.round === 1 ? 600 : 400, opacity: p.round === 2 ? 0.8 : 1 }}>
                          {p.round === 1 ? '1st' : '2nd'}
                          {' — '}
                          {p.status === 'own' && !p.note && 'Own pick'}
                          {p.status === 'own' && p.note && p.note}
                          {p.status === 'traded' && `Traded to ${p.to}`}
                          {p.status === 'incoming' && `From ${p.from}`}
                          {p.note && p.status !== 'own' ? ` (${p.note})` : ''}
                        </span>
                      </td>
                      <td>
                        {p.status === 'own' && <span className="badge badge-muted">{p.swapWith ? 'SWAP' : 'OWN'}</span>}
                        {p.status === 'traded' && <span className="badge" style={{ background: 'var(--accent-red)', color: '#fff' }}>OUT</span>}
                        {p.status === 'incoming' && <span className="badge" style={{ background: 'var(--accent-green)', color: '#fff' }}>IN</span>}
                      </td>
                    </tr>
                  ));
                })}
              </tbody>
            </table>
            {teamPickData.stepien && (
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--accent-red)', marginTop: 6, letterSpacing: 1 }}>
                STEPIEN RULE — Cannot trade 1sts in consecutive years
              </div>
            )}
            {tpes.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: 2, color: 'var(--text-dim)', marginBottom: 6 }}>TRADE EXCEPTIONS</div>
                {tpes.map((tpe, i) => (
                  <div key={i} className="player-bio-row">
                    <span className="player-bio-label">{tpe.from}</span>
                    <span className="player-bio-value" style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>
                      ${(tpe.amount / 1000000).toFixed(1)}M — expires {tpe.expires}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {selectedPlayer && (
        <PlayerModal
          player={selectedPlayer}
          teamAbbr={teamAbbr}
          onClose={() => setSelectedPlayer(null)}
          onWaive={onWaivePlayer ? (p) => setWaiveTarget(p) : null}
          onExtend={onExtendPlayer || null}
        />
      )}
      {waiveTarget && onWaivePlayer && (
        <WaiveModal
          player={waiveTarget}
          teamAbbr={teamAbbr}
          onClose={() => setWaiveTarget(null)}
          onConfirm={(playerName, stretch) => {
            onWaivePlayer(teamAbbr, playerName, stretch);
            setWaiveTarget(null);
            setSelectedPlayer(null);
          }}
        />
      )}

      {/* Share Build Modal */}
      {showShareBuild && hasSalary && (() => {
        const players = (salaryTeam.players || []).filter(p => !p.isDead && !p.isTwoWay);
        const starters = players
          .map(p => ({ ...p, ovr: p.ovr || getPlayerRating(p.name) || 70 }))
          .sort((a, b) => b.ovr - a.ovr)
          .slice(0, 5);
        const bench = players
          .map(p => ({ ...p, ovr: p.ovr || getPlayerRating(p.name) || 70 }))
          .sort((a, b) => b.ovr - a.ovr)
          .slice(5);
        const avgOvr = players.length > 0
          ? Math.round(players.reduce((s, p) => s + (p.ovr || getPlayerRating(p.name) || 70), 0) / players.length)
          : 0;

        const shareText = [
          `${team.name} Roster Build`,
          `Payroll: ${formatSalary(totalSalary)} | Cap Space: ${overCap ? '-' : ''}${formatSalary(Math.abs(capSpace))} | ${inTax ? 'In Tax' : 'Under Tax'}`,
          '',
          'Starters:',
          ...starters.map(p => `  ${p.name} (${p.ovr} OVR) - ${formatSalary(p.salary2526)}`),
          '',
          'Bench:',
          ...bench.slice(0, 8).map(p => `  ${p.name} (${p.ovr} OVR) - ${formatSalary(p.salary2526)}`),
          bench.length > 8 ? `  +${bench.length - 8} more` : '',
          '',
          `Avg OVR: ${avgOvr} | Roster: ${players.length} players`,
          '',
          'Built with hoops4thought.com',
        ].filter(Boolean).join('\n');

        const copyToClipboard = () => {
          navigator.clipboard?.writeText(shareText).then(() => {
            const btn = document.getElementById('share-copy-btn');
            if (btn) { btn.textContent = 'Copied'; setTimeout(() => { btn.textContent = 'Copy to Clipboard'; }, 2000); }
          }).catch(() => {});
        };

        const shareToTwitter = () => {
          const tweet = `Check out my ${team.name} build on @Hoops4Thought!\n\nPayroll: ${formatSalary(totalSalary)} | Avg OVR: ${avgOvr}\nTop 5: ${starters.map(p => p.name).join(', ')}\n\nhoops4thought.com`;
          window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(tweet)}`, '_blank');
        };

        return (
          <div className="modal-overlay" onClick={() => setShowShareBuild(false)}>
            <div className="share-build-modal" onClick={e => e.stopPropagation()}>
              <button className="modal-close" onClick={() => setShowShareBuild(false)}>&times;</button>

              <div className="share-build-header">
                <img src={teamLogoUrl(team.nbaId)} alt="" className="share-build-logo" />
                <div>
                  <div className="share-build-team">{team.name}</div>
                  <div className="share-build-label">ROSTER BUILD</div>
                </div>
              </div>

              <div className="share-build-stats">
                <div className="share-build-stat">
                  <div className="share-build-stat-label">Payroll</div>
                  <div className="share-build-stat-value">{formatSalary(totalSalary)}</div>
                </div>
                <div className="share-build-stat">
                  <div className="share-build-stat-label">Cap Space</div>
                  <div className="share-build-stat-value">{overCap ? '-' : ''}{formatSalary(Math.abs(capSpace))}</div>
                </div>
                <div className="share-build-stat">
                  <div className="share-build-stat-label">Avg OVR</div>
                  <div className="share-build-stat-value">{avgOvr}</div>
                </div>
                <div className="share-build-stat">
                  <div className="share-build-stat-label">Roster</div>
                  <div className="share-build-stat-value">{players.length}</div>
                </div>
              </div>

              <div className="share-build-section">
                <div className="share-build-section-title">Starters</div>
                {starters.map(p => (
                  <div key={p.name} className="share-build-player">
                    <span className="share-build-player-ovr" style={{ color: getRatingColor(p.ovr) }}>{p.ovr}</span>
                    <span className="share-build-player-name">{p.name}</span>
                    <span className="share-build-player-sal">{formatSalary(p.salary2526)}</span>
                  </div>
                ))}
              </div>

              <div className="share-build-section">
                <div className="share-build-section-title">Bench</div>
                {bench.slice(0, 8).map(p => (
                  <div key={p.name} className="share-build-player">
                    <span className="share-build-player-ovr" style={{ color: getRatingColor(p.ovr) }}>{p.ovr}</span>
                    <span className="share-build-player-name">{p.name}</span>
                    <span className="share-build-player-sal">{formatSalary(p.salary2526)}</span>
                  </div>
                ))}
                {bench.length > 8 && (
                  <div className="share-build-player" style={{ color: 'var(--text-dim)' }}>
                    +{bench.length - 8} more players
                  </div>
                )}
              </div>

              <div className="share-build-actions">
                <button id="share-copy-btn" className="share-build-action-btn" onClick={copyToClipboard}>Copy to Clipboard</button>
                <button className="share-build-action-btn twitter" onClick={shareToTwitter}>Share on X</button>
              </div>

              <div className="share-build-watermark">hoops4thought.com</div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

// ============================================================================
// PLACEHOLDER PAGES
// ============================================================================

const DraftPage = ({ salaryData }) => (
  <DraftBoard salaryData={salaryData} />
);

// ============================================================================
// PLAYER SEARCH
// ============================================================================
const PlayerSearch = ({ salaryData, onSelectTeam }) => {
  const [query, setQuery] = useState('');
  const sd = salaryData || SALARY_DATA;

  const results = useMemo(() => {
    if (!query || query.length < 2) return [];
    const q = query.toLowerCase();
    const found = [];
    for (const [abbr, team] of Object.entries(sd)) {
      if (!team?.players) continue;
      for (const p of team.players) {
        if (p.isDead) continue;
        if (p.name.toLowerCase().includes(q)) {
          found.push({ ...p, teamAbbr: abbr });
        }
      }
    }
    return found.sort((a, b) => (b.salary2526 || 0) - (a.salary2526 || 0)).slice(0, 30);
  }, [query, sd]);

  return (
    <div className="fade-in" style={{ maxWidth: 700, margin: '0 auto' }}>
      <div className="section-label" style={{ marginBottom: 12 }}>Player Search</div>
      <input
        className="draft-search"
        placeholder="Search for any player..."
        value={query}
        onChange={e => setQuery(e.target.value)}
        autoFocus
        style={{ width: '100%', maxWidth: 400, marginBottom: 16 }}
      />
      {results.length > 0 ? (
        <table className="roster-table">
          <thead>
            <tr>
              <th>Player</th>
              <th>Rating</th>
              <th>Team</th>
              <th>Pos</th>
              <th className="right">Salary</th>
              <th className="right">Status</th>
            </tr>
          </thead>
          <tbody>
            {results.map(p => {
              const ovr = p.ovr || getPlayerRating(p.name);
              const rColor = getRatingColor(ovr);
              const ext = getExtensionStatus(p);
              return (
                <tr key={`${p.teamAbbr}-${p.name}`} onClick={() => onSelectTeam(p.teamAbbr)} style={{ cursor: 'pointer' }}>
                  <td>
                    <div className="player-cell">
                      <PlayerHeadshot name={p.name} nbaComId={p.nbaComId} />
                      <div>
                        <div className="player-name">
                          {p.name}
                          {p.isTwoWay && <span className="badge badge-muted" style={{ marginLeft: 6, fontSize: 8 }}>2-WAY</span>}
                          {ext && <span className="ext-badge" style={{ borderColor: ext.color, color: ext.color }}>{ext.label}</span>}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td><span className="ovr-badge" style={{ borderLeftColor: rColor }}>{ovr}</span></td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <img src={teamLogoUrl(getTeam(p.teamAbbr)?.nbaId)} alt="" style={{ width: 18, height: 18 }} />
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{p.teamAbbr}</span>
                    </div>
                  </td>
                  <td><span className="badge badge-muted">{p.position}</span></td>
                  <td className="right"><span className="salary">{formatSalary(p.salary2526)}</span></td>
                  <td className="right">
                    <span className="salary" style={{ color: p.salary2627 ? 'var(--text)' : 'var(--accent-red)' }}>
                      {p.salary2627 ? `${formatSalary(p.salary2627)} next yr` : 'UFA'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : query.length >= 2 ? (
        <div style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', padding: 40 }}>
          No players found matching "{query}"
        </div>
      ) : (
        <div style={{ fontSize: 13, color: 'var(--text-dim)', textAlign: 'center', padding: 40 }}>
          Type at least 2 characters to search
        </div>
      )}
    </div>
  );
};

// ============================================================================
// TRANSACTION HISTORY
// ============================================================================
const TX_ICONS = { trade: '\u21C4', ai_trade: '\u21C4', waive: '\u2716', signing: '\u270D', draft: '\u2605', extension: '\u2B06', retirement: '\u2302' };
const TX_COLORS = { trade: 'var(--accent-gold)', ai_trade: '#D4A843', waive: 'var(--accent-red)', signing: 'var(--accent-green)', draft: '#7B2FF7', extension: '#1E90FF', retirement: '#8B6914' };

const TransactionHistory = ({ transactionLog }) => {
  const [expandedIdx, setExpandedIdx] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const allLog = (transactionLog || []).slice().reverse();
  
  // Filter by search term (player names, team names, summaries)
  const log = searchTerm.trim() ? allLog.filter(tx => {
    const s = searchTerm.toLowerCase();
    const fields = [tx.summary, tx.player, tx.team, tx.team1, tx.team2,
      ...(tx.team1Sends || []), ...(tx.team2Sends || [])].filter(Boolean);
    return fields.some(f => String(f).toLowerCase().includes(s));
  }) : allLog;

  const formatDate = (ts) => {
    const d = new Date(ts);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const phaseLabel = (phase) => {
    const map = { regular: 'Regular Season', postDeadline: 'Post-Deadline', playoffs: 'Playoffs', draft: 'Draft', offseason: 'Offseason' };
    return map[phase] || phase;
  };

  return (
    <div className="fade-in" style={{ maxWidth: 700, margin: '0 auto' }}>
      <div className="section-label" style={{ marginBottom: 12 }}>Transaction History</div>
      <div style={{ marginBottom: 12 }}>
        <input
          type="text"
          placeholder="Search player or team name..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          style={{ width: '100%', padding: '8px 12px', fontSize: 12, borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-main)', color: 'var(--text-primary)', fontFamily: 'var(--font-sans)' }}
        />
      </div>
      {log.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--text-dim)', textAlign: 'center', padding: 40 }}>
          No transactions yet. Trades, signings, waivers, and draft picks will appear here.
        </div>
      ) : (
        <div className="tx-list">
          {log.map((tx, i) => {
            const icon = TX_ICONS[tx.type] || '\u25CF';
            const color = TX_COLORS[tx.type] || 'var(--text-muted)';
            const yearLabel = tx.seasonYear ? `${tx.seasonYear}-${String(tx.seasonYear + 1).slice(2)}` : '';
            const isExpanded = expandedIdx === i;
            return (
              <div key={i} className={`tx-row ${isExpanded ? 'expanded' : ''}`} onClick={() => setExpandedIdx(isExpanded ? null : i)}>
                <div className="tx-icon" style={{ color }}>{icon}</div>
                <div className="tx-body">
                  <div className="tx-summary">{tx.summary}</div>
                  <div className="tx-meta">
                    <span className="tx-type-badge" style={{ borderColor: color, color }}>{tx.type.toUpperCase()}</span>
                    <span>{phaseLabel(tx.phase)}</span>
                    {yearLabel && <span>{yearLabel}</span>}
                    <span>{formatDate(tx.timestamp)}</span>
                  </div>
                  {isExpanded && tx.type === 'trade' && tx.packages && (
                    <div className="tx-details">
                      {(() => {
                        // Handle both formats:
                        // OLD: tx.packages = { packages: {MIN:[...], MIL:[...]}, picks:{...}, teams:[...], ... }
                        // NEW: tx.packages = { MIN: [...players], MIL: [...players] }
                        let teamPlayerMap = tx.packages;
                        if (tx.packages.packages && typeof tx.packages.packages === 'object' && !Array.isArray(tx.packages.packages)) {
                          teamPlayerMap = tx.packages.packages;
                        }
                        // Filter out non-team keys (picks, pickDestinations, teams, timestamp, etc)
                        const teamEntries = Object.entries(teamPlayerMap).filter(([key, val]) => 
                          Array.isArray(val) && val.length > 0 && typeof val[0] === 'object' && val[0]?.name
                        );
                        if (teamEntries.length === 0) {
                          // Last resort: show summary text
                          return <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{tx.summary || 'Trade details unavailable'}</div>;
                        }
                        return teamEntries.map(([team, players]) => (
                          <div key={team} style={{ marginBottom: 8 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                              {getTeam(team)?.nbaId && <img src={teamLogoUrl(getTeam(team).nbaId)} alt="" style={{width:14,height:14}} />}
                              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--accent-gold)', letterSpacing: 1 }}>{getTeam(team)?.name || team} SENDS</span>
                            </div>
                            {players.map((p, j) => (
                              <div key={j} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 11, padding: '2px 0', paddingLeft: 20 }}>
                                <span style={{ fontWeight: 500 }}>{p.name}</span>
                                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-dim)' }}>{formatSalary(p.salary2526 || p.salary || 0)}</span>
                              </div>
                            ))}
                          </div>
                        ));
                      })()}
                    </div>
                  )}
                  {isExpanded && tx.type === 'ai_trade' && (
                    <div className="tx-details">
                      {tx.team1 && tx.team2 && (
                        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                              {getTeam(tx.team1)?.nbaId && <img src={teamLogoUrl(getTeam(tx.team1).nbaId)} alt="" style={{width:14,height:14}} />}
                              <span style={{ fontWeight: 600, fontSize: 10 }}>{getTeam(tx.team1)?.name || tx.team1} send:</span>
                            </div>
                            {(tx.team1Sends || []).map((n, j) => <div key={j} style={{fontSize:11,color:'var(--text-muted)',paddingLeft:18}}>{n}</div>)}
                          </div>
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                              {getTeam(tx.team2)?.nbaId && <img src={teamLogoUrl(getTeam(tx.team2).nbaId)} alt="" style={{width:14,height:14}} />}
                              <span style={{ fontWeight: 600, fontSize: 10 }}>{getTeam(tx.team2)?.name || tx.team2} send:</span>
                            </div>
                            {(tx.team2Sends || []).map((n, j) => <div key={j} style={{fontSize:11,color:'var(--text-muted)',paddingLeft:18}}>{n}</div>)}
                          </div>
                        </div>
                      )}
                      {!tx.team1 && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{tx.summary}</div>}
                    </div>
                  )}
                  {isExpanded && tx.player && (
                    <div className="tx-details">
                      <div className="player-bio-row"><span className="player-bio-label">Player</span><span className="player-bio-value">{tx.player}</span></div>
                      {tx.team && <div className="player-bio-row"><span className="player-bio-label">Team</span><span className="player-bio-value">{tx.team}</span></div>}
                      {tx.salary && <div className="player-bio-row"><span className="player-bio-label">Salary</span><span className="player-bio-value">{formatSalary(tx.salary)}</span></div>}
                      {tx.years && <div className="player-bio-row"><span className="player-bio-label">Years</span><span className="player-bio-value">{tx.years}</span></div>}
                      {tx.pick && <div className="player-bio-row"><span className="player-bio-label">Pick</span><span className="player-bio-value">#{tx.pick}</span></div>}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// SAVE SYSTEM
// ============================================================================
// ============================================================================
// LEAGUE NEWS SIDEBAR — Article-style feed for AI trades & transactions
// ============================================================================
const LeagueNewsSidebar = ({ aiTradeLog, transactionLog, newsFeed, onClose }) => {
  const [expanded, setExpanded] = useState(null);
  const newsItems = useMemo(() => {
    const items = [];
    if (newsFeed && newsFeed.length > 0) {
      for (const n of newsFeed) {
        items.push({
          id: n.id,
          type: n.type === 'blockbuster_trade' ? 'blockbuster' : n.type,
          headline: n.headline,
          body: n.body,
          teams: n.teams || [],
          time: n.timestamp,
        });
      }
    } else {
      for (const t of (aiTradeLog || [])) {
        const t1 = getTeam(t.team1);
        const t2 = getTeam(t.team2);
        items.push({
          id: `trade-${t.timestamp}`,
          type: t.type === 'blockbuster' ? 'blockbuster' : 'trade',
          headline: t.type === 'blockbuster'
            ? `${t1?.name || t.team1} and ${t2?.name || t.team2} Complete Blockbuster Deal`
            : `${t1?.name || t.team1} Acquire ${t.team2Sends.map(p => p.name).join(', ')} in Trade`,
          body: `The ${t1?.name || t.team1} have agreed to send ${t.team1Sends.map(p => p.name).join(' and ')} to the ${t2?.name || t.team2} in exchange for ${t.team2Sends.map(p => p.name).join(' and ')}.`,
          teams: [t.team1, t.team2],
          time: t.timestamp,
        });
      }
    }
    for (const tx of (transactionLog || []).filter(t => t.type === 'signing' || t.type === 'waive').slice(0, 10)) {
      items.push({
        id: `tx-${tx.timestamp}`,
        type: tx.type,
        headline: tx.type === 'signing' ? `${tx.team || 'Team'} Signs Free Agent` : `${tx.team || 'Team'} Waives Player`,
        body: tx.summary || '',
        teams: tx.team ? [tx.team] : [],
        time: tx.timestamp,
      });
    }
    return items.sort((a, b) => {
      // Blockbusters always first
      if (a.type === 'blockbuster' && b.type !== 'blockbuster') return -1;
      if (b.type === 'blockbuster' && a.type !== 'blockbuster') return 1;
      return b.time - a.time;
    }).slice(0, 30);
  }, [aiTradeLog, transactionLog, newsFeed]);

  if (newsItems.length === 0) return null;

  const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    const now = new Date();
    const diffH = Math.floor((now - d) / 3600000);
    if (diffH < 1) return 'Just now';
    if (diffH < 24) return `${diffH}h ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="news-section">
      <div className="news-section-header">
        <div className="news-section-title">League Transactions</div>
        {onClose && <button className="news-section-close" onClick={onClose} title="Dismiss">&times;</button>}
      </div>
      <div className="news-section-list">
        {newsItems.map((item, i) => {
          const isBlockbuster = item.type === 'blockbuster';
          const isTrade = item.type === 'trade' || isBlockbuster;
          const isExp = expanded === i;
          const teamLogos = (item.teams || []).map(abbr => getTeam(abbr)).filter(Boolean);
          return (
            <article
              key={item.id}
              className={`news-card ${isBlockbuster ? 'news-card-breaking' : isTrade ? 'news-card-trade' : 'news-card-signing'}`}
              onClick={() => setExpanded(isExp ? null : i)}
            >
              <div className="news-card-accent" />
              <div className="news-card-content">
                <div className="news-card-category">
                  {isBlockbuster ? 'BREAKING' : isTrade ? 'TRADE' : item.type === 'signing' ? 'SIGNING' : 'TRANSACTION'}
                </div>
                <h3 className="news-card-headline">{item.headline}</h3>
                {item.body && (
                  <p className="news-card-body">{item.body}</p>
                )}
                <div className="news-card-meta">
                  {teamLogos.length > 0 && (
                    <div className="news-card-teams">
                      {teamLogos.map(t => (
                        <img key={t.abbr} src={teamLogoUrl(t.nbaId)} alt={t.abbr} className="news-card-logo" />
                      ))}
                    </div>
                  )}
                  <span className="news-card-time">{formatTime(item.time)}</span>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
};

// ============================================================================
// COUNTER OFFER MODAL — AI teams propose trades to the user
// ============================================================================
const CounterOfferModal = ({ offers, onAccept, onDecline, myTeam }) => {
  const [viewIdx, setViewIdx] = useState(0);
  if (!offers || offers.length === 0) return null;
  const offer = offers[viewIdx];
  if (!offer) return null;
  const proposer = getTeam(offer.fromTeam);
  const myTeamInfo = getTeam(myTeam);

  return (
    <div className="modal-overlay" onClick={() => onDecline(viewIdx)}>
      <div className="counter-offer-modal" onClick={e => e.stopPropagation()}>
        <div className="counter-offer-header">
          <div className="counter-offer-label">INCOMING TRADE PROPOSAL</div>
          <div className="counter-offer-count">{viewIdx + 1} of {offers.length}</div>
        </div>
        <div className="counter-offer-body">
          <div className="counter-offer-team">
            {proposer?.nbaId && <img src={teamLogoUrl(proposer.nbaId)} alt="" className="counter-offer-logo" />}
            <div>
              <div className="counter-offer-team-name">{proposer?.name || offer.fromTeam} offer</div>
              <div className="counter-offer-players">
                {offer.theyOffer.map((p, i) => (
                  <div key={i} className="counter-offer-player">
                    <span className="counter-offer-player-name">{p.name}</span>
                    <span className="counter-offer-player-ovr" style={{ color: getRatingColor(p.ovr) }}>{p.ovr}</span>
                    <span className="counter-offer-player-sal">{formatSalary(p.salary)}</span>
                  </div>
                ))}
              </div>
              <div className="counter-offer-total">Their package</div>
            </div>
          </div>
          <div className="counter-offer-divider">FOR</div>
          <div className="counter-offer-team">
            {myTeamInfo?.nbaId && <img src={teamLogoUrl(myTeamInfo.nbaId)} alt="" className="counter-offer-logo" />}
            <div>
              <div className="counter-offer-team-name">{myTeamInfo?.name || myTeam} send</div>
              <div className="counter-offer-players">
                {offer.theyWant.map((p, i) => (
                  <div key={i} className="counter-offer-player">
                    <span className="counter-offer-player-name">{p.name}</span>
                    <span className="counter-offer-player-ovr" style={{ color: getRatingColor(p.ovr) }}>{p.ovr}</span>
                    <span className="counter-offer-player-sal">{formatSalary(p.salary)}</span>
                  </div>
                ))}
              </div>
              <div className="counter-offer-total">Your package</div>
            </div>
          </div>
        </div>
        <div className="counter-offer-value-bar">
          <div className={`counter-offer-value-label ${offer.theirValue >= offer.yourValue ? 'favorable' : 'unfavorable'}`}>
            {offer.theirValue > offer.yourValue * 1.1 ? 'FAVORABLE DEAL' : offer.theirValue >= offer.yourValue * 0.9 ? 'FAIR DEAL' : 'UNFAVORABLE DEAL'}
          </div>
        </div>
        <div className="counter-offer-actions">
          {offers.length > 1 && viewIdx > 0 && <button className="counter-offer-nav" onClick={() => setViewIdx(viewIdx - 1)}>Prev</button>}
          <button className="counter-offer-decline" onClick={() => onDecline(viewIdx)}>Decline</button>
          <button className="counter-offer-accept" onClick={() => onAccept(viewIdx)}>Accept Trade</button>
          {offers.length > 1 && viewIdx < offers.length - 1 && <button className="counter-offer-nav" onClick={() => setViewIdx(viewIdx + 1)}>Next</button>}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// SEASON RECAP EXPORT
// ============================================================================
function exportSeasonRecap(activeSave, currentSalaryData) {
  const canvas = document.createElement('canvas');
  canvas.width = 800;
  canvas.height = 600;
  const ctx = canvas.getContext('2d');

  // Background
  ctx.fillStyle = '#0a0a0a';
  ctx.fillRect(0, 0, 800, 600);
  ctx.fillStyle = '#c9a227';
  ctx.fillRect(0, 0, 800, 4);

  // Title
  ctx.fillStyle = '#fff';
  ctx.font = 'bold 28px sans-serif';
  ctx.textAlign = 'center';
  const year = activeSave.seasonYear || 2025;
  ctx.fillText(`${year}-${String(year + 1).slice(2)} Season Recap`, 400, 50);

  ctx.font = '10px monospace';
  ctx.fillStyle = '#888';
  ctx.fillText('HOOPS FOR THOUGHT', 400, 75);

  // Champion
  const po = activeSave.seasonState?.playoffs;
  if (po?.champion) {
    const champ = getTeam(po.champion);
    ctx.fillStyle = '#c9a227';
    ctx.font = 'bold 20px sans-serif';
    ctx.fillText('NBA CHAMPION', 400, 120);
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 32px sans-serif';
    ctx.fillText(champ?.name || po.champion, 400, 160);
    if (po.finals) {
      ctx.font = '16px sans-serif';
      ctx.fillStyle = '#ccc';
      ctx.fillText(`Finals: ${po.finals.home} ${po.finals.homeWins} - ${po.finals.awayWins} ${po.finals.away}`, 400, 190);
    }
  }

  // User team record
  const myTeam = activeSave.myTeam;
  if (myTeam) {
    const myInfo = getTeam(myTeam);
    const standings = activeSave.seasonState?.finalStandings;
    let record = null;
    if (standings) {
      const all = [...(standings.east || []), ...(standings.west || [])];
      record = all.find(t => t.abbr === myTeam);
    }
    ctx.fillStyle = '#c9a227';
    ctx.font = '10px monospace';
    ctx.fillText('YOUR TEAM', 400, 230);
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 22px sans-serif';
    ctx.fillText(myInfo?.name || myTeam, 400, 258);
    if (record) {
      ctx.font = '18px sans-serif';
      ctx.fillStyle = '#ccc';
      ctx.fillText(`${record.wins}-${record.losses}`, 400, 285);
    }
  }

  // Transactions summary
  const txLog = activeSave.transactionLog || [];
  const yearTx = txLog.filter(t => t.seasonYear === year);
  const trades = yearTx.filter(t => t.type === 'trade' || t.type === 'ai_trade').length;
  const signings = yearTx.filter(t => t.type === 'signing').length;
  const draftPicks = yearTx.filter(t => t.type === 'draft').length;

  ctx.textAlign = 'center';
  ctx.fillStyle = '#666';
  ctx.font = '10px monospace';
  ctx.fillText('SEASON TRANSACTIONS', 400, 330);

  const stats = [
    { label: 'TRADES', val: trades },
    { label: 'SIGNINGS', val: signings },
    { label: 'DRAFT PICKS', val: draftPicks },
  ];
  stats.forEach((s, i) => {
    const x = 200 + i * 200;
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 28px sans-serif';
    ctx.fillText(s.val, x, 375);
    ctx.fillStyle = '#888';
    ctx.font = '10px monospace';
    ctx.fillText(s.label, x, 395);
  });

  // Top moves
  ctx.textAlign = 'left';
  ctx.fillStyle = '#666';
  ctx.font = '10px monospace';
  ctx.fillText('  KEY MOVES', 40, 435);
  const keyMoves = yearTx.filter(t => t.type === 'trade' || t.type === 'signing' || t.type === 'draft').slice(0, 5);
  keyMoves.forEach((tx, i) => {
    ctx.fillStyle = '#aaa';
    ctx.font = '13px sans-serif';
    const text = (tx.summary || '').substring(0, 85);
    ctx.fillText(text, 40, 460 + i * 22);
  });

  // Footer
  ctx.textAlign = 'center';
  ctx.fillStyle = '#444';
  ctx.font = '10px sans-serif';
  ctx.fillText('Generated by Hoops For Thought  |  hoopsforthought.com', 400, 585);

  // Download
  const link = document.createElement('a');
  link.download = `hft-season-recap-${year}.png`;
  link.href = canvas.toDataURL('image/png');
  link.click();
}

const SAVE_KEY = 'hft-gm-saves';
const MAX_SAVES = 5;

const getDefaultSave = (name, opts = {}) => ({
  name: name || 'New Save',
  mode: opts.mode || 'sandbox',
  myTeam: opts.myTeam || null,
  aiBots: opts.aiBots || false,
  aiTradesEnabled: opts.aiTradesEnabled !== undefined ? opts.aiTradesEnabled : (opts.mode === 'season'),
  aiTradeLog: [],
  salaryData: JSON.parse(JSON.stringify(SALARY_DATA)),
  tradeHistory: [],
  transactionLog: [],
  freeAgentPool: initPool(null, SALARY_DATA),
  draftState: null,
  seasonPhase: 'regular',
  seasonState: opts.mode === 'season' && opts.myTeam ? { schedule: generateSchedule(), playoffs: null, complete: false } : null,
  seasonYear: 2025,
  createdAt: Date.now(),
  updatedAt: Date.now(),
});

const loadSaves = () => {
  try {
    const raw = localStorage.getItem(SAVE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) { console.error('Failed to load saves:', e); }
  return null;
};

const persistSaves = (saves, activeIndex) => {
  try {
    localStorage.setItem(SAVE_KEY, JSON.stringify({ saves, activeIndex }));
  } catch (e) { console.error('Failed to persist saves:', e); }
};

// ============================================================================
// NEW SAVE MODAL
// ============================================================================
const NewSaveModal = ({ onClose, onCreate }) => {
  const [name, setName] = useState('');
  const [mode, setMode] = useState('sandbox');
  const [myTeam, setMyTeam] = useState('');
  const [aiBots, setAiBots] = useState(true);

  const handleCreate = () => {
    const saveName = name.trim() || `Save ${Date.now() % 1000}`;
    onCreate(saveName, { mode, aiBots, aiTradesEnabled: mode === 'season' ? aiBots : false, myTeam: mode === 'season' ? myTeam : null });
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content new-save-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>&times;</button>

        <div className="new-save-header">
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 8, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--text-muted)' }}>New Save File</div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 24, marginTop: 4 }}>Create Your Season</div>
        </div>

        {/* Name Input */}
        <div className="new-save-field">
          <label className="new-save-label">Save Name</label>
          <input
            className="new-save-input"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="My GM Season"
            autoFocus
            onKeyDown={e => { if (e.key === 'Enter') handleCreate(); }}
          />
        </div>

        {/* Mode Selection */}
        <div className="new-save-field">
          <label className="new-save-label">Mode</label>
          <div className="new-save-modes">
            <button
              className={`new-save-mode-card ${mode === 'sandbox' ? 'active' : ''}`}
              onClick={() => setMode('sandbox')}
            >
              <div className="mode-card-title">GM Mode</div>
              <div className="mode-card-desc">Real-time rosters, salaries, and CBA rules. Build trades, manage your cap, explore scenarios with today's data.</div>
              <div className="mode-card-tag recommended">Recommended</div>
            </button>
            <button
              className={`new-save-mode-card ${mode === 'season' ? 'active' : ''}`}
              onClick={() => setMode('season')}
            >
              <div className="mode-card-title">Season Sim</div>
              <div className="mode-card-desc">Simulate games and advance through the season. Manage a full campaign from tip-off to the Finals.</div>
              <div className="mode-card-tag">Beta</div>
            </button>
          </div>
        </div>

        {/* Team Picker (Season Sim only) */}
        {mode === 'season' && (
          <div className="new-save-field">
            <label className="new-save-label">Your Team</label>
            <div className="save-team-grid">
              {TEAMS.map(t => (
                <button
                  key={t.abbr}
                  className={`save-team-btn ${myTeam === t.abbr ? 'active' : ''}`}
                  onClick={() => setMyTeam(t.abbr)}
                  title={t.name}
                >
                  <img src={teamLogoUrl(t.nbaId)} alt={t.abbr} className="save-team-logo" />
                </button>
              ))}
            </div>
            {myTeam && <div className="save-team-selected">{TEAMS.find(t => t.abbr === myTeam)?.name}</div>}
          </div>
        )}

        {/* AI Trades Toggle — Season Sim only */}
        {mode === 'season' && (
        <div className="new-save-field">
          <div className="new-save-toggle-row">
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <label className="new-save-label" style={{ marginBottom: 0 }}>AI League Trades</label>
              </div>
              <div className="new-save-toggle-desc">Other teams make 17-25 trades per season. You'll receive counter offers from AI teams you can accept or decline.</div>
            </div>
            <button
              className={`toggle-switch ${aiBots ? 'on' : ''}`}
              onClick={() => setAiBots(!aiBots)}
            >
              <div className="toggle-knob" />
            </button>
          </div>
        </div>
        )}

        {/* Create Button */}
        <div className="new-save-actions">
          <button className="new-save-cancel" onClick={onClose}>Cancel</button>
          <button className="new-save-create" onClick={handleCreate} disabled={mode === 'season' && !myTeam}>
            Create Save →
          </button>
        </div>
      </div>
    </div>
  );
};

const SaveBar = ({ saves, activeIndex, onSelect, onCreate, onDelete, onRename, onReset }) => {
  const [open, setOpen] = useState(false);
  const [renaming, setRenaming] = useState(null);
  const [renameName, setRenameName] = useState('');
  const ref = useRef(null);
  const active = saves[activeIndex];

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const startRename = (i, currentName) => {
    setRenaming(i);
    setRenameName(currentName);
  };

  const finishRename = () => {
    if (renaming !== null && renameName.trim()) {
      onRename(renaming, renameName.trim());
    }
    setRenaming(null);
  };

  return (
    <div className="save-dropdown-wrap" ref={ref}>
      <button className="save-dropdown-trigger" onClick={() => setOpen(!open)}>
        
        <div className="save-trigger-info">
          <span className="save-trigger-name">{active?.name || 'My GM Season'}</span>
          <span className="save-trigger-meta">
            <span className="save-mode-tag">{active?.mode === 'season' ? 'Season Sim' : 'GM Mode'}</span>
            {active?.myTeam ? ` \u00B7 ${active.myTeam}` : ''}
          </span>
        </div>
        <span className="save-trigger-arrow">{open ? '\u25B2' : '\u25BC'}</span>
      </button>
      {open && (
        <div className="save-dropdown-panel">
          <div className="save-dropdown-title">SAVE FILES</div>
          <div className="save-dropdown-hint">Switch between saves or create a new one to change modes</div>
          {saves.map((save, i) => (
            <div key={i} className={`save-dropdown-slot ${i === activeIndex ? 'active' : ''}`}>
              {renaming === i ? (
                <input
                  className="save-rename-input"
                  value={renameName}
                  onChange={(e) => setRenameName(e.target.value)}
                  onBlur={finishRename}
                  onKeyDown={(e) => { if (e.key === 'Enter') finishRename(); if (e.key === 'Escape') setRenaming(null); }}
                  autoFocus
                />
              ) : (
                <button className="save-dropdown-slot-btn" onClick={() => { onSelect(i); setOpen(false); }}>
                  <span className="save-slot-name">{save.name}</span>
                  <span className="save-slot-meta">
                    <span className="save-mode-tag">{save.mode === 'season' ? 'Season Sim' : 'GM Mode'}</span>
                    {save.myTeam ? ` \u00B7 ${save.myTeam}` : ''}
                    {save.tradeHistory.length > 0 ? ` \u00B7 ${save.tradeHistory.length} trade${save.tradeHistory.length > 1 ? 's' : ''}` : ''}
                  </span>
                </button>
              )}
              <div className="save-slot-actions">
                <button className="save-action-btn" onClick={() => startRename(i, save.name)} title="Rename">&#9998;</button>
                {saves.length > 1 && <button className="save-action-btn delete" onClick={() => onDelete(i)} title="Delete">&times;</button>}
              </div>
            </div>
          ))}
          <div className="save-dropdown-footer">
            {saves.length < MAX_SAVES && (
              <button className="save-new-btn" onClick={onCreate}>+ New Save File</button>
            )}
            <button className="save-reset-btn" onClick={onReset}>Reset</button>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// APP
// ============================================================================
function App() {
  const [page, setPage] = useState('teams');
  const [showProgressionPopup, setShowProgressionPopup] = useState(false);
  const [txHistorySearch, setTxHistorySearch] = useState('');
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [draftTradeOpen, setDraftTradeOpen] = useState(false);
  const [depthChartOpen, setDepthChartOpen] = useState(null); // team abbr or null
  const { standings, loading: standingsLoading } = useStandings();

  // Theme state
  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem('hft-theme') || 'light'; } catch { return 'light'; }
  });
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try { localStorage.setItem('hft-theme', theme); } catch {}
  }, [theme]);
  const toggleTheme = useCallback(() => setTheme(t => t === 'dark' ? 'light' : 'dark'), []);

  // Save system state
  const [saves, setSaves] = useState(() => {
    const loaded = loadSaves();
    if (loaded?.saves?.length > 0) return loaded.saves;
    return [getDefaultSave('My GM Season')];
  });
  const [activeIndex, setActiveIndex] = useState(() => {
    const loaded = loadSaves();
    return loaded?.activeIndex || 0;
  });

  const activeSave = saves[activeIndex] || saves[0];
  if (!activeSave.freeAgentPool) activeSave.freeAgentPool = initPool(null, activeSave.salaryData);
  // Restore rating overrides from save (player progression between seasons)
  // SANITIZER: Only strip clearly corrupted values from old broken progression system
  if (activeSave.ratingOverrides && typeof window !== 'undefined') {
    const cleaned = {};
    const sYear = activeSave.seasonYear || 2025;
    const yearsPlayed = Math.max(0, sYear - 2025);
    for (const [name, ovr] of Object.entries(activeSave.ratingOverrides)) {
      if (ovr < 65 || ovr > 99) continue; // Out of valid range
      const base = getBasePlayerRating(name);
      if (base === null) {
        // Unknown player (drafted rookie etc) — keep override
        cleaned[name] = ovr;
        continue;
      }
      // Allow generous drift: ±6 per year played, minimum ±8 always
      // This catches old corruption (Edwards 95→78 = -17) but preserves valid progression
      const maxDrift = Math.max(8, (yearsPlayed + 1) * 6);
      if (Math.abs(ovr - base) <= maxDrift) {
        cleaned[name] = ovr;
      }
      // Otherwise: corrupted, drop it silently
    }
    window.__ratingOverrides = cleaned;
  }
  const currentSalaryData = activeSave.salaryData;
  const currentFreeAgentPool = filterRosteredPlayers(activeSave.freeAgentPool || [], currentSalaryData);
  const tradeHistory = activeSave.tradeHistory;
  const seasonPhase = activeSave.seasonPhase || 'regular';
  const isSeasonMode = activeSave.mode === 'season' && activeSave.myTeam;
  // Global team control: GM mode = locked to myTeam, sandbox = user picks
  const [controlTeam, setControlTeam] = useState(activeSave.myTeam || '');
  const activeControlTeam = isSeasonMode ? activeSave.myTeam : controlTeam;

  // Persist on every change
  useEffect(() => {
    persistSaves(saves, activeIndex);
  }, [saves, activeIndex]);

  const updateActiveSave = useCallback((updates) => {
    setSaves(prev => {
      const next = [...prev];
      next[activeIndex] = { ...next[activeIndex], ...updates, updatedAt: Date.now() };
      return next;
    });
  }, [activeIndex]);

  // Transaction logger — every GM move gets recorded
  const logTransaction = useCallback((type, details) => {
    setSaves(prev => {
      const next = [...prev];
      const save = { ...next[activeIndex] };
      const log = [...(save.transactionLog || [])];
      log.push({ type, ...details, timestamp: Date.now(), phase: save.seasonPhase, seasonYear: save.seasonYear || 2025 });
      save.transactionLog = log;
      save.updatedAt = Date.now();
      next[activeIndex] = save;
      return next;
    });
  }, [activeIndex]);

  const handleUpdateSeason = useCallback((seasonState) => {
    updateActiveSave({ seasonState });
  }, [updateActiveSave]);

  // Depth chart save handler
  const handleSaveDepthChart = useCallback((teamAbbr, chart) => {
    const current = activeSave.depthCharts || {};
    updateActiveSave({ depthCharts: { ...current, [teamAbbr]: chart } });
  }, [updateActiveSave, activeSave.depthCharts]);

  const handleNav = useCallback((p) => { setPage(p); setSelectedTeam(null); window.scrollTo(0, 0); }, []);
  const handleSelectTeam = useCallback((abbr) => { setSelectedTeam(abbr); window.scrollTo(0, 0); }, []);
  const handleBack = useCallback(() => { setSelectedTeam(null); window.scrollTo(0, 0); }, []);
  const handleHome = useCallback(() => { setPage('teams'); setSelectedTeam(null); window.scrollTo(0, 0); }, []);

  const handleTradeExecuted = useCallback((newSalaryData, tradePkgs) => {
    // Track draft pick ownership changes for the current draft year
    const currentYear = (activeSave.seasonYear || 2025) + 1; // Draft year is next year
    const overrides = { ...(activeSave.draftPickOverrides || {}) };
    if (tradePkgs?.picks) {
      const pickPkgs = tradePkgs.picks;
      const teams = tradePkgs.teams || [];

      // Build full draft order (applying existing overrides first)
      let draftOrder;
      const standings = activeSave.standings;
      if (standings) {
        const allTeams = [...(standings.east || []), ...(standings.west || [])];
        allTeams.sort((a, b) => (a.wins / Math.max(1, a.wins + a.losses)) - (b.wins / Math.max(1, b.wins + b.losses)));
        draftOrder = allTeams.slice(0, 30).map((t, i) => ({ pick: i + 1, team: t.abbr, via: null, round: 1 }));
      } else {
        draftOrder = DRAFT_ORDER_2026.map(s => ({ ...s, round: 1 }));
      }
      // Apply existing overrides
      for (const slot of draftOrder) {
        if (overrides[slot.pick]) {
          slot.via = slot.team;
          slot.team = overrides[slot.pick];
        }
      }
      // Add R2 slots
      const r2 = draftOrder.map((s, i) => ({ pick: 31 + i, team: s.team, via: s.via, round: 2 }));
      const fullOrder = [...draftOrder, ...r2];

      // For each team sending picks, find the receiver and map to slot number
      for (const senderAbbr of Object.keys(pickPkgs)) {
        for (const pick of (pickPkgs[senderAbbr] || [])) {
          if (pick.year !== currentYear || !pick.round) continue;

          // Determine receiver: in 2-team trade it's the other team
          // In 3+ team trades, use pickDestinations from the trade record
          let receiver;
          if (teams.length === 2) {
            receiver = teams.find(t => t !== senderAbbr);
          } else {
            // Look up destination from pickDestinations: { senderAbbr: { pickId: receiverAbbr } }
            const pDests = tradePkgs.pickDestinations || {};
            const senderDests = pDests[senderAbbr] || {};
            // Try to match by pick id
            receiver = senderDests[pick.id] || Object.values(senderDests)[0] || teams.find(t => t !== senderAbbr);
          }

          if (!receiver) continue;

          // Find the matching draft slot
          const isRound1 = pick.round === 1;
          const slotsInRound = fullOrder.filter(s => isRound1 ? s.pick <= 30 : s.pick > 30);

          let matchedSlot = null;
          if (pick.status === 'incoming' && pick.from) {
            // Incoming pick — find slot where the original team appears
            const fromTeam = pick.from.split('/')[0]; // Handle "NOP/POR" formats
            matchedSlot = slotsInRound.find(s =>
              s.team === senderAbbr && (s.via === fromTeam || s.via?.includes(fromTeam))
            ) || slotsInRound.find(s =>
              s.via === fromTeam || (s.team === fromTeam && !overrides[s.pick])
            );
          }
          if (!matchedSlot) {
            // Own pick or couldn't find incoming match — find sender's own slot
            matchedSlot = slotsInRound.find(s => s.team === senderAbbr && !s.via)
              || slotsInRound.find(s => s.team === senderAbbr);
          }

          if (matchedSlot) {
            overrides[matchedSlot.pick] = receiver;
          }
        }
      }
    }
    updateActiveSave({
      salaryData: newSalaryData,
      tradeHistory: [...tradeHistory, tradePkgs],
      draftPickOverrides: overrides,
    });
    if (tradePkgs) {
      // Build clean trade summary for history
      const cleanPackages = {};
      if (tradePkgs.packages) {
        for (const [team, players] of Object.entries(tradePkgs.packages)) {
          cleanPackages[team] = (Array.isArray(players) ? players : []).map(p => ({
            name: p.name, salary2526: p.salary2526 || p.salary || 0
          }));
        }
      }
      const teams = tradePkgs.teams || Object.keys(cleanPackages);
      const summaryParts = teams.map(t => `${t}: ${(cleanPackages[t] || []).map(p => p.name).join(', ')}`);
      logTransaction('trade', {
        summary: `Trade: ${summaryParts.join(' for ')}`,
        packages: cleanPackages,
        teams,
      });
    }
  }, [updateActiveSave, tradeHistory, logTransaction, activeSave.seasonYear, activeSave.draftPickOverrides, activeSave.standings]);

  const handleWaivePlayer = useCallback((teamAbbr, playerName, stretch) => {
    const sd = currentSalaryData || SALARY_DATA;
    const result = executeWaiveFA(sd, teamAbbr, playerName, stretch);
    if (result) {
      const newPool = addToPool(currentFreeAgentPool, result.freeAgent);
      updateActiveSave({
        salaryData: result.newSalaryData,
        freeAgentPool: newPool,
      });
      logTransaction('waive', { player: playerName, team: teamAbbr, stretch, summary: `${teamAbbr} waived ${playerName}${stretch ? ' (stretch)' : ''}` });
    }
  }, [currentSalaryData, updateActiveSave, currentFreeAgentPool, logTransaction]);

  // Pure function: compute offseason pool from salary data (no closures)
  const computeOffseasonData = (salaryDataInput, existingPool) => {
    const sd = JSON.parse(JSON.stringify(salaryDataInput));
    const newPool = [...existingPool];
    let ufaCount = 0;
    for (const [abbr, team] of Object.entries(sd)) {
      if (!team?.players) continue;
      const remaining = [];
      for (const p of team.players) {
        // Expiring: has current-year salary, no next-year salary, not dead cap or two-way
        if (!p.isDead && !p.isTwoWay && p.salary2526 > 0 && !p.salary2627) {
          const ovr = p.ovr || getPlayerRating(p.name) || 70;
          // RFA heuristic: young player (salary under $12M = likely rookie scale) or age < 26
          const isRookieScale = p.salary2526 < 12000000;
          const estimatedAge = getPlayerAgeSafe(p.name, p.salary2526);
          const isYoung = estimatedAge < 26;
          const isRFA = isRookieScale && isYoung;
          if (!newPool.some(fa => fa.name === p.name)) {
            newPool.push({
              name: p.name,
              nbaComId: p.nbaComId || 0,
              position: PLAYER_POSITIONS[p.name] || p.position || 'SF',
              age: null,
              lastTeam: abbr,
              type: isRFA ? 'RFA' : 'UFA',
              reason: 'expired',
              ovr,
              priorSalary: p.salary2526,
              addedAt: Date.now(),
              matchingTeam: isRFA ? abbr : null, // Team with matching rights
            });
            ufaCount++;
          }
          // Don't add to remaining — removed from roster
        } else {
          remaining.push(p);
        }
      }
      team.players = remaining;
      team.payroll = remaining.reduce((s, p) => s + (p.salary2526 || 0), 0);
    }
    return { salaryData: sd, freeAgentPool: newPool, ufaCount };
  };

  // Pure function: auto-simulate entire 60-pick draft (used when draft phase is skipped)
  const autoSimDraft = (salaryDataInput) => {
    const sd = JSON.parse(JSON.stringify(salaryDataInput));
    const draftYear = (activeSave.seasonYear || 2025) + 1;
    const prospectList = getDraftProspects(draftYear);
    // Register ages for draft prospects (default to 19-20 year olds)
    registerDraftClass(prospectList, draftYear - 20);
    // Filter out players already on rosters (prevents cross-year duplicates)
    const rostered = new Set();
    for (const abbr of Object.keys(sd)) {
      if (sd[abbr]?.players) {
        for (const p of sd[abbr].players) { if (p.name) rostered.add(p.name); }
      }
    }
    const available = prospectList.filter(p => !rostered.has(p.name));
    const results = [];

    // Build draft order from standings (current or previous season)
    const draftStandings = activeSave.seasonState?.finalStandings || activeSave.seasonState?.prevSeasonStandings || null;
    let r1;
    if (draftStandings) {
      const allTeams = [...(draftStandings.east || []), ...(draftStandings.west || [])];
      allTeams.sort((a, b) => (a.wins / Math.max(1, a.wins + a.losses)) - (b.wins / Math.max(1, b.wins + b.losses)));
      r1 = allTeams.slice(0, 30).map((t, i) => ({ pick: i + 1, team: t.abbr, round: 1 }));
    } else {
      r1 = DRAFT_ORDER_2026.map(s => ({ pick: s.pick, team: s.team, round: 1 }));
    }
    const order = [
      ...r1,
      ...r1.map((s, i) => ({ pick: i + 31, team: s.team, round: 2 })),
    ];

    // Apply pick trades from tradeHistory
    for (const trade of (tradeHistory || [])) {
      if (!trade.picks) continue;
      const teams = trade.teams || [];
      for (const [sender, sentPicks] of Object.entries(trade.picks)) {
        for (const pick of (sentPicks || [])) {
          if (pick.year !== draftYear || !pick.round) continue;
          const receiver = teams.find(t => t !== sender) || '';
          if (!receiver) continue;
          const isR1 = pick.round === 1;
          const slot = order.find(s => s.team === sender && (isR1 ? s.pick <= 30 : s.pick > 30));
          if (slot) { slot.team = receiver; }
        }
      }
    }

    order.sort((a, b) => a.pick - b.pick);

    for (const slot of order) {
      if (available.length === 0) break;
      // Simple AI: pick highest-ranked available with positional need bonus
      const needs = {};
      const td = sd[slot.team];
      if (td?.players) {
        const posCounts = { PG: 0, SG: 0, SF: 0, PF: 0, C: 0 };
        for (const p of td.players) {
          if (p.isDead) continue;
          const pos = PLAYER_POSITIONS[p.name] || 'SF';
          if (posCounts[pos] !== undefined) posCounts[pos]++;
        }
        for (const [pos, count] of Object.entries(posCounts)) {
          needs[pos] = count < 2 ? 2 : count < 3 ? 1 : 0;
        }
      }

      let bestIdx = 0;
      let bestScore = -Infinity;
      const pickNum = slot.pick;
      for (let i = 0; i < available.length; i++) {
        const p = available[i];
        let score = 100 - p.rank;
        const positions = p.pos.split('/');
        for (const pos of positions) {
          const mapped = pos === 'G' ? 'PG' : pos === 'F' ? 'SF' : pos;
          score += (needs[mapped] || 0) * 5;
        }
        // Peterson #1 hard-lock
        if (p.name === 'Darryn Peterson' && pickNum === 1) score += 500;
        // Dybantsa top-5 protection
        if (p.name === 'AJ Dybantsa' && pickNum <= 5) score += 200;
        // Controlled variance by pick range
        let varianceRange = pickNum <= 5 ? 0 : pickNum <= 8 ? 3 : pickNum <= 13 ? 6 : pickNum <= 30 ? 12 : 16;
        score += (Math.random() - 0.5) * varianceRange;
        if (score > bestScore) { bestScore = score; bestIdx = i; }
      }

      const prospect = available.splice(bestIdx, 1)[0];
      const contract = buildRookieContract(prospect.name, slot.pick, prospect.ovr);
      if (sd[slot.team]) {
        sd[slot.team].players.push(contract);
        sd[slot.team].payroll = sd[slot.team].players.reduce((s, p) => s + (p.salary2526 || 0), 0);
      }
      results.push({ pick: slot.pick, team: slot.team, prospect, salary: contract.salary2627 || 0 });
    }
    return { salaryData: sd, results };
  };

  // Helper: estimate player age from name hash (for players without explicit age)
  const handleBeginOffseason = useCallback((standings) => {
    const sd = JSON.parse(JSON.stringify(currentSalaryData || SALARY_DATA));
    let salaryDataForOffseason = sd;
    let draftResults = activeSave.draftState?.results || [];

    // If draft was never run (no draftState or empty results), auto-sim it
    if (!draftResults || draftResults.length === 0) {
      const autoDraft = autoSimDraft(sd);
      salaryDataForOffseason = autoDraft.salaryData;
      draftResults = autoDraft.results;
    } else {
      // Draft was played — apply results to salary data (rookies need to be added to rosters)
      for (const pick of draftResults) {
        if (!pick.prospect || !sd[pick.team]) continue;
        // Check if rookie already on roster (handleDraftComplete may have already added them)
        const already = sd[pick.team].players.some(p => p.name === pick.prospect.name);
        if (!already) {
          const contract = buildRookieContract(pick.prospect.name, pick.pick, pick.prospect.ovr);
          sd[pick.team].players.push(contract);
          sd[pick.team].payroll = sd[pick.team].players.reduce((s, p) => s + (p.salary2526 || 0), 0);
        }
      }
      salaryDataForOffseason = sd;
    }

    const pool = activeSave.freeAgentPool || [];

    // Apply player progression (OVR changes between seasons)
    const nextSeasonYear = (activeSave.seasonYear || 2025) + 1;
    const progressionReport = [];
    const retirements = [];
    // Track players drafted THIS season (no progression for them)
    const draftedThisSeason = new Set(
      (activeSave.draftState?.results || []).map(r => r.prospect?.name).filter(Boolean)
    );
    
    for (const abbr of Object.keys(salaryDataForOffseason)) {
      const team = salaryDataForOffseason[abbr];
      if (!team?.players) continue;
      const retiredPlayers = [];
      for (const p of team.players) {
        if (p.isDead || !p.name) continue;
        const age = getPlayerAgeSafe(p.name, p.salary2526, nextSeasonYear);
        
        // Retirement check: 40+ retires (LeBron at 43)
        const retireAge = p.name === 'LeBron James' ? 43 : 40;
        if (age >= retireAge) {
          retiredPlayers.push(p);
          retirements.push({ name: p.name, team: abbr, age, salary: p.salary2526 });
          continue;
        }
        
        const isDrafted = draftedThisSeason.has(p.name);
        const oldOvr = getPlayerRating(p.name) || 70;
        const newOvr = progressPlayer(p.name, oldOvr, age, nextSeasonYear, isDrafted);
        if (newOvr !== oldOvr) {
          if (!window.__ratingOverrides) window.__ratingOverrides = {};
          window.__ratingOverrides[p.name] = newOvr;
          progressionReport.push({ name: p.name, team: abbr, oldOvr, newOvr, change: newOvr - oldOvr });
        }
      }
      // Remove retired players from roster
      if (retiredPlayers.length > 0) {
        team.players = team.players.filter(p => !retiredPlayers.some(r => r.name === p.name));
        team.payroll = team.players.reduce((s, p) => s + (p.salary2526 || 0), 0);
      }
    }
    // Log retirements
    for (const r of retirements) {
      logTransaction('retirement', {
        player: r.name, team: r.team,
        summary: `${r.name} has announced his retirement from the NBA at age ${r.age}. Thank you for everything.`,
      });
    }
    // Also progress FA pool players
    for (const fa of pool) {
      const oldOvr = getPlayerRating(fa.name) || 70;
      const age = getPlayerAgeSafe(fa.name, fa.priorSalary, nextSeasonYear);
      const newOvr = progressPlayer(fa.name, oldOvr, age, nextSeasonYear, false);
      if (newOvr !== oldOvr) {
        if (!window.__ratingOverrides) window.__ratingOverrides = {};
        window.__ratingOverrides[fa.name] = newOvr;
      }
    }

    const result = computeOffseasonData(salaryDataForOffseason, pool);
    const seasonState = activeSave.seasonState || {};
    updateActiveSave({
      seasonPhase: 'offseason',
      salaryData: result.salaryData,
      freeAgentPool: result.freeAgentPool,
      draftState: { currentPick: draftResults.length, results: draftResults },
      seasonState: { ...seasonState, finalStandings: standings || seasonState.finalStandings || null },
      ratingOverrides: window.__ratingOverrides || {},
      lastProgressionReport: progressionReport.filter(p => Math.abs(p.change) >= 1).slice(0, 40),
      lastRetirements: retirements,
    });
    setPage('freeAgency');
  }, [currentSalaryData, activeSave.freeAgentPool, activeSave.seasonState, activeSave.draftState, updateActiveSave]);

  const handleSignPlayer = useCallback((playerName, salary, years, signingTeam) => {
    const sd = JSON.parse(JSON.stringify(currentSalaryData || SALARY_DATA));
    const team = signingTeam || activeControlTeam;
    if (!team || !sd[team]) return;

    const isMidSeason = seasonPhase === 'regular' || seasonPhase === 'postDeadline';
    // Preserve headshot: look up nbaComId from FA pool
    const faEntry = currentFreeAgentPool.find(fa => fa.name === playerName);
    const nbaComId = faEntry?.nbaComId || 0;

    const newPlayer = isMidSeason ? {
      name: playerName, nbaComId,
      salary2526: salary,
      salary2627: years >= 2 ? Math.round(salary * 1.05) : 0,
      salary2728: years >= 3 ? Math.round(salary * 1.05 * 1.05) : 0,
      salary2829: years >= 4 ? Math.round(salary * Math.pow(1.05, 3)) : 0,
      option: '',
    } : {
      name: playerName, nbaComId,
      salary2526: 0,
      salary2627: salary,
      salary2728: years >= 2 ? Math.round(salary * 1.05) : 0,
      salary2829: years >= 3 ? Math.round(salary * 1.05 * 1.05) : 0,
      option: years >= 4 ? `P 2029-30` : '',
    };

    sd[team].players.push(newPlayer);
    sd[team].payroll = sd[team].players.reduce((s, p) => s + (p.salary2526 || 0), 0);
    const newPool = currentFreeAgentPool.filter(fa => fa.name !== playerName);

    updateActiveSave({ salaryData: sd, freeAgentPool: newPool });
    logTransaction('signing', { player: playerName, team, salary, years, midSeason: isMidSeason, summary: `${team} signed ${playerName} (${years}yr / ${formatSalary(salary)})` });
  }, [currentSalaryData, activeSave.myTeam, currentFreeAgentPool, updateActiveSave, seasonPhase, logTransaction]);

  // --- EXTENSION HANDLER ---
  const handleExtendPlayer = useCallback((teamAbbr, playerName, newYears, newSalary) => {
    const sd = JSON.parse(JSON.stringify(currentSalaryData || SALARY_DATA));
    const team = sd[teamAbbr];
    if (!team) return;
    const playerIdx = team.players.findIndex(p => p.name === playerName);
    if (playerIdx === -1) return;
    const player = { ...team.players[playerIdx] };

    // Find where current contract ends and add extension years
    const slots = ['salary2627', 'salary2728', 'salary2829'];
    let lastFilledIdx = -1;
    for (let i = 0; i < slots.length; i++) {
      if (player[slots[i]] > 0) lastFilledIdx = i;
    }
    // Fill from lastFilledIdx + 1 onwards
    let baseSalary = newSalary;
    for (let i = 0; i < newYears && (lastFilledIdx + 1 + i) < slots.length; i++) {
      const slotIdx = lastFilledIdx + 1 + i;
      player[slots[slotIdx]] = Math.round(baseSalary * Math.pow(1.05, i));
    }
    // Clear the Q/T options since they're being extended
    player.option = '';

    team.players[playerIdx] = player;
    team.payroll = team.players.reduce((s, p) => s + (p.salary2526 || 0), 0);
    updateActiveSave({ salaryData: sd });
    logTransaction('extension', { player: playerName, team: teamAbbr, years: newYears, salary: newSalary, summary: `${teamAbbr} extended ${playerName} (${newYears}yr / ${formatSalary(newSalary)})` });
  }, [currentSalaryData, updateActiveSave, logTransaction]);

  // --- DRAFT HANDLERS ---
  const handleBeginDraft = useCallback(() => {
    updateActiveSave({
      seasonPhase: 'draft',
      draftState: { currentPick: 0, results: [] },
    });
    setPage('draft');
  }, [updateActiveSave]);

  // Draft pick handler — only logs transactions, does NOT update salary data per-pick
  // (Updating per-pick causes stale closure: each rapid pick reads same base data, losing 59 of 60 rookies)
  const handleDraftPick = useCallback((pickResult) => {
    if (pickResult.isUserPick) {
      logTransaction('draft', { player: pickResult.prospect.name, team: pickResult.team, pick: pickResult.pick, summary: `${pickResult.team} drafted ${pickResult.prospect.name} (#${pickResult.pick})` });
    }
  }, [logTransaction]);

  // Save draft progress so user can navigate away and come back
  const handleDraftProgress = useCallback((pickIndex, results) => {
    updateActiveSave({
      draftState: { ...(activeSave.draftState || {}), currentPick: pickIndex, results },
    });
  }, [updateActiveSave, activeSave.draftState]);

  // Open full trade machine during draft
  const handleDraftOpenTrade = useCallback(() => {
    setDraftTradeOpen(true);
  }, []);

  // Close trade machine and return to draft
  const handleDraftCloseTrade = useCallback(() => {
    setDraftTradeOpen(false);
  }, []);

  const handleTradePick = useCallback((tradeData) => {
    const sd = JSON.parse(JSON.stringify(currentSalaryData || SALARY_DATA));
    const myTeam = activeControlTeam;
    if (!myTeam || !sd[myTeam]) return;

    if (tradeData.returning?.type === 'players' && tradeData.tradedTo) {
      const partner = tradeData.tradedTo;
      for (const playerName of (tradeData.returning.players || [])) {
        const pIdx = sd[partner].players.findIndex(p => p.name === playerName);
        if (pIdx !== -1) {
          const [player] = sd[partner].players.splice(pIdx, 1);
          sd[myTeam].players.push(player);
        }
      }
      sd[myTeam].payroll = sd[myTeam].players.reduce((s, p) => s + (p.salary2526 || 0), 0);
      sd[partner].payroll = sd[partner].players.reduce((s, p) => s + (p.salary2526 || 0), 0);
    }

    // Save pick ownership override so the draft knows who owns this pick
    const overrides = { ...(activeSave.draftPickOverrides || {}) };
    if (tradeData.pick && tradeData.tradedTo) {
      overrides[tradeData.pick] = tradeData.tradedTo;
    }

    updateActiveSave({ salaryData: sd, draftPickOverrides: overrides });
    logTransaction('trade', {
      summary: `${myTeam} traded pick #${tradeData.pick} to ${tradeData.tradedTo}`,
      packages: { [myTeam]: [], [tradeData.tradedTo]: tradeData.returning?.players || [] },
      picks: { [myTeam]: [{ round: tradeData.pick <= 30 ? 1 : 2, year: (activeSave.seasonYear || 2025) + 1 }] },
      teams: [myTeam, tradeData.tradedTo],
    });
  }, [currentSalaryData, activeControlTeam, updateActiveSave, activeSave.draftPickOverrides, activeSave.seasonYear, logTransaction]);

  const handleDraftComplete = useCallback((results) => {
    // ATOMIC: Apply ALL 60 draft picks to salary data, THEN compute offseason pool
    const sd = JSON.parse(JSON.stringify(currentSalaryData || SALARY_DATA));

    // Step 1: Add every drafted rookie to their team's roster
    for (const pick of results) {
      if (!pick.prospect || !sd[pick.team]) continue;
      const contract = buildRookieContract(pick.prospect.name, pick.pick, pick.prospect.ovr);
      sd[pick.team].players.push(contract);
      sd[pick.team].payroll = sd[pick.team].players.reduce((s, p) => s + (p.salary2526 || 0), 0);
    }

    // Step 2: Compute offseason FA pool (remove expiring contracts, add to pool)
    const pool = activeSave.freeAgentPool || [];
    const offseason = computeOffseasonData(sd, pool);
    const seasonState = activeSave.seasonState || {};

    updateActiveSave({
      draftState: { currentPick: results.length, results },
      seasonPhase: 'offseason',
      salaryData: offseason.salaryData,
      freeAgentPool: offseason.freeAgentPool,
      seasonState: { ...seasonState, finalStandings: seasonState.finalStandings || null },
    });
    setPage('freeAgency');
  }, [currentSalaryData, activeSave.freeAgentPool, activeSave.seasonState, updateActiveSave]);

  // --- SEASON ADVANCE ---
  const handleAdvanceSeason = useCallback(() => {
    const sd = currentSalaryData || SALARY_DATA;
    const advanced = advanceSeason(sd, null);
    const newSd = advanced.salaryData;

    // Distribute unsigned FAs to teams using CBA-accurate signing rules
    const remainingPool = currentFreeAgentPool || [];
    const signed = new Set();
    const faSignings = []; // Track for transaction history
    const MIN_ROSTER = 14;

    // Track which exceptions each team has used
    const mleUsed = {};    // team -> amount used of MLE
    const baeUsed = {};    // team -> true if BAE used
    const MLE_FULL = 14104000;   // Non-taxpayer MLE
    const MLE_TAX = 5685000;     // Taxpayer MLE
    const BAE = 5135000;         // Bi-annual exception

    // Sort FAs by quality (best first)
    const sortedFAs = [...remainingPool]
      .filter(fa => !fa.isTwoWay)
      .sort((a, b) => (b.ovr || 65) - (a.ovr || 65));

    // Helper: what can a team offer?
    const getMaxOffer = (abbr) => {
      const team = newSd[abbr];
      if (!team?.players) return 0;
      const payroll = team.players.reduce((s, p) => s + (p.salary2526 || 0), 0);
      const capSpace = CBA.salaryCap - payroll;
      const rosterSize = team.players.filter(p => !p.isDead && !p.isTwoWay).length;
      if (rosterSize >= 15) return 0;

      // Under cap — can offer up to remaining cap space
      if (capSpace > 0) return capSpace;

      // Over cap but under tax — non-taxpayer MLE available
      if (payroll <= CBA.luxuryTax) {
        const mleRemaining = MLE_FULL - (mleUsed[abbr] || 0);
        if (mleRemaining > CBA.minSalary) return mleRemaining;
      }

      // In tax but under first apron — taxpayer MLE
      if (payroll <= CBA.firstApron) {
        const mleRemaining = MLE_TAX - (mleUsed[abbr] || 0);
        if (mleRemaining > CBA.minSalary) return mleRemaining;
        // BAE available if under first apron and not used
        if (!baeUsed[abbr]) return BAE;
      }

      // Above first apron — vet minimum only
      return CBA.minSalary;
    };

    // Generate market-value signings
    for (const fa of sortedFAs) {
      if (signed.has(fa.name)) continue;
      const ovr = fa.ovr || 65;
      if (ovr < 68) continue;

      const offerData = generateOffers(fa, newSd, null);
      const offers = offerData.offers || [];
      if (offers.length === 0) continue;

      let placed = false;

      // Try each AI-suggested team — but only sign at what the team can actually offer
      // NEVER auto-sign to user's team in season sim
      for (const offer of offers) {
        const targetTeam = offer.team;
        if (!newSd[targetTeam]) continue;
        if (isSeasonMode && targetTeam === activeSave.myTeam) continue;
        const maxOffer = getMaxOffer(targetTeam);
        if (maxOffer < CBA.minSalary) continue;

        const payroll = newSd[targetTeam].players.reduce((s, p) => s + (p.salary2526 || 0), 0);
        const capSpace = CBA.salaryCap - payroll;
        let salary;
        let years = offer.years || 1;

        if (capSpace >= offer.salary) {
          // Has full cap space — sign at market value
          salary = offer.salary;
        } else if (capSpace > CBA.minSalary) {
          // Partial cap space — use available room
          salary = Math.min(offer.salary, capSpace);
        } else {
          // Over cap — use exception amount, capped at what team has
          salary = Math.min(offer.salary, maxOffer);
          // MLE deals cap at 4 years, min deals at 2 years
          if (salary <= CBA.minSalary + 500000) years = Math.min(years, 2);
          else years = Math.min(years, 4);
        }

        // Star players won't sign for vet minimums — skip this team
        if (ovr >= 78 && salary < 5000000) continue;
        if (ovr >= 73 && salary < CBA.minSalary) continue;

        newSd[targetTeam].players.push({
          name: fa.name, nbaComId: fa.nbaComId || 0,
          salary2526: salary,
          salary2627: years >= 2 ? Math.round(salary * 1.05) : 0,
          salary2728: years >= 3 ? Math.round(salary * 1.05 * 1.05) : 0,
          salary2829: 0, option: '',
        });
        newSd[targetTeam].payroll = newSd[targetTeam].players.reduce((s, p) => s + (p.salary2526 || 0), 0);
        signed.add(fa.name);
        faSignings.push({ player: fa.name, team: targetTeam, salary, years });
        placed = true;

        // Track exception usage
        if (capSpace <= 0) {
          if (salary <= BAE && !baeUsed[targetTeam] && payroll <= CBA.firstApron) {
            baeUsed[targetTeam] = true;
          } else {
            mleUsed[targetTeam] = (mleUsed[targetTeam] || 0) + salary;
          }
        }
        break;
      }

      // Fallback: find any team with cap space for quality FAs
      if (!placed && ovr >= 75) {
        const capTeams = Object.entries(newSd)
          .filter(([abbr]) => !(isSeasonMode && abbr === activeSave.myTeam))
          .map(([abbr, team]) => ({ abbr, capSpace: CBA.salaryCap - team.players.reduce((s, p) => s + (p.salary2526 || 0), 0), roster: team.players.filter(p => !p.isDead && !p.isTwoWay).length }))
          .filter(t => t.capSpace > 5000000 && t.roster < 15)
          .sort((a, b) => b.capSpace - a.capSpace);

        for (const t of capTeams) {
          const salary = Math.min(offers[0]?.salary || 10000000, t.capSpace);
          if (ovr >= 78 && salary < 5000000) continue;
          const years = offers[0]?.years || 1;
          newSd[t.abbr].players.push({
            name: fa.name, nbaComId: fa.nbaComId || 0,
            salary2526: salary,
            salary2627: years >= 2 ? Math.round(salary * 1.05) : 0,
            salary2728: years >= 3 ? Math.round(salary * 1.05 * 1.05) : 0,
            salary2829: 0, option: '',
          });
          newSd[t.abbr].payroll = newSd[t.abbr].players.reduce((s, p) => s + (p.salary2526 || 0), 0);
          signed.add(fa.name);
          faSignings.push({ player: fa.name, team: t.abbr, salary, years });
          break;
        }
      }
    }

    // Second pass: fill teams below minimum roster with remaining FAs (skip user team)
    for (const [abbr, team] of Object.entries(newSd)) {
      if (!team?.players) continue;
      if (isSeasonMode && abbr === activeSave.myTeam) continue;
      const rosterSize = team.players.filter(p => !p.isDead && !p.isTwoWay).length;
      if (rosterSize >= MIN_ROSTER) continue;

      const spotsNeeded = MIN_ROSTER - rosterSize;
      let filled = 0;
      for (const fa of sortedFAs) {
        if (filled >= spotsNeeded) break;
        if (signed.has(fa.name)) continue;
        // Vet minimum scales with experience (approximated by OVR)
        const ovr = fa.ovr || 65;
        const vetMin = ovr >= 75 ? 3300000 : ovr >= 70 ? 2900000 : 2200000;
        newSd[abbr].players.push({
          name: fa.name,
          nbaComId: fa.nbaComId || 0,
          salary2526: vetMin,
          salary2627: 0, salary2728: 0, salary2829: 0, option: '',
        });
        signed.add(fa.name);
        faSignings.push({ player: fa.name, team: abbr, salary: vetMin, years: 1 });
        filled++;
      }
      if (filled > 0) {
        newSd[abbr].payroll = newSd[abbr].players.reduce((s, p) => s + (p.salary2526 || 0), 0);
      }
    }

    // Keep unsigned FAs in the pool for the new season
    const unsignedPool = remainingPool.filter(fa => !signed.has(fa.name));

    // Log notable FA signings (OVR 70+) to transaction history
    for (const s of faSignings) {
      if ((getPlayerRating(s.player) || 65) >= 70) {
        logTransaction('signing', {
          player: s.player, team: s.team, salary: s.salary, years: s.years,
          summary: `${s.team} signed ${s.player} (${s.years}yr / ${formatSalary(s.salary)}) in free agency`,
          offseason: true,
        });
      }
    }

    // Build retirement news for the new season
    const retirementNews = (activeSave.lastRetirements || []).map(r => ({
      id: `news-retire-${r.name}`,
      type: 'retirement',
      headline: `${r.name} retires from the NBA`,
      body: `${r.name} has announced his retirement from the NBA at age ${r.age} after a legendary career. Thank you for everything.`,
      teams: [r.team],
      timestamp: Date.now(),
    }));

    updateActiveSave({
      salaryData: newSd,
      seasonState: { ...advanced.seasonState, prevSeasonStandings: activeSave.seasonState?.finalStandings || null },
      seasonPhase: 'regular',
      freeAgentPool: [...unsignedPool, ...initPool(null, newSd).filter(p => !unsignedPool.some(u => u.name === p.name))],
      draftState: null,
      draftPickOverrides: {}, // Clear slot overrides for new season
      seasonYear: (activeSave.seasonYear || 2025) + 1,
      // Reset for fresh season — new trades will populate these
      aiTradeLog: [],
      newsFeed: retirementNews,
      pendingTradeOffers: [],
    });
    // Show progression popup for user's team
    if (activeSave.lastProgressionReport?.length > 0) {
      setShowProgressionPopup(true);
    }
    setPage('season');
  }, [currentSalaryData, activeSave.draftState, activeSave.seasonYear, currentFreeAgentPool, updateActiveSave, logTransaction]);

  const handleSelectSave = useCallback((i) => {
    setActiveIndex(i);
    setSelectedTeam(null);
  }, []);

  const [showNewSaveModal, setShowNewSaveModal] = useState(false);
  const [showWelcome, setShowWelcome] = useState(true);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const dismissWelcome = () => {
    setShowWelcome(false);
    if (dontShowAgain) { try { localStorage.setItem('hft_hide_welcome', '1'); } catch {} }
  };
  // Auto-hide if user previously checked "don't show again"
  useEffect(() => {
    try { if (localStorage.getItem('hft_hide_welcome') === '1') setShowWelcome(false); } catch {}
  }, []);
  const [showHelp, setShowHelp] = useState(false);
  const [showHelpTip, setShowHelpTip] = useState(false);
  const [helpStartTutorial, setHelpStartTutorial] = useState(false);
  // Show the help tooltip bubble after a short delay on first visit
  useEffect(() => {
    let dismissed = false;
    try { dismissed = localStorage.getItem('hft_help_tip_seen') === '1'; } catch {}
    if (dismissed) return;
    const timer = setTimeout(() => setShowHelpTip(true), 3000);
    return () => clearTimeout(timer);
  }, []);
  const dismissHelpTip = () => {
    setShowHelpTip(false);
    try { localStorage.setItem('hft_help_tip_seen', '1'); } catch {}
  };

  // News sidebar state
  const [showNewsSidebar, setShowNewsSidebar] = useState(true);
  const [lastSeenNewsCount, setLastSeenNewsCount] = useState(0);

  // Re-show news section when new trades/transactions arrive
  const newsTradeCount = (activeSave.aiTradeLog || []).length + (activeSave.newsFeed || []).length + (activeSave.transactionLog || []).length;
  useEffect(() => {
    if (newsTradeCount > lastSeenNewsCount && newsTradeCount > 0) {
      setShowNewsSidebar(true);
      setLastSeenNewsCount(newsTradeCount);
    }
  }, [newsTradeCount, lastSeenNewsCount]);
  // Counter offer state
  const [showCounterOffers, setShowCounterOffers] = useState(false);
  const pendingOffers = activeSave.pendingTradeOffers || [];

  // Show counter offers automatically when they arrive
  useEffect(() => {
    if (pendingOffers.length > 0 && isSeasonMode) {
      setShowCounterOffers(true);
    }
  }, [pendingOffers.length, isSeasonMode]);

  const handleAcceptOffer = useCallback((offerIdx) => {
    const offers = activeSave.pendingTradeOffers || [];
    const offer = offers[offerIdx];
    if (!offer) return;
    const sd = JSON.parse(JSON.stringify(currentSalaryData || SALARY_DATA));
    const success = executeCounterOffer(offer, sd, activeSave.myTeam);
    if (success) {
      const remaining = offers.filter((_, i) => i !== offerIdx);
      logTransaction('trade', {
        team1: offer.fromTeam, team2: activeSave.myTeam,
        team1Sends: offer.theyOffer.map(p => p.name),
        team2Sends: offer.theyWant.map(p => p.name),
        summary: `Accepted: ${offer.fromTeam} traded ${offer.theyOffer.map(p => p.name).join(', ')} to ${activeSave.myTeam} for ${offer.theyWant.map(p => p.name).join(', ')}`,
      });
      const aiLog = [...(activeSave.aiTradeLog || []), {
        team1: offer.fromTeam, team2: activeSave.myTeam,
        team1Sends: offer.theyOffer, team2Sends: offer.theyWant,
        type: 'counter_accepted', timestamp: Date.now(),
      }];
      updateActiveSave({ salaryData: sd, pendingTradeOffers: remaining, aiTradeLog: aiLog });
      if (remaining.length === 0) setShowCounterOffers(false);
    }
  }, [currentSalaryData, activeSave, updateActiveSave, logTransaction]);

  const handleDeclineOffer = useCallback((offerIdx) => {
    const offers = activeSave.pendingTradeOffers || [];
    const remaining = offers.filter((_, i) => i !== offerIdx);
    updateActiveSave({ pendingTradeOffers: remaining });
    if (remaining.length === 0) setShowCounterOffers(false);
  }, [activeSave.pendingTradeOffers, updateActiveSave]);

  const handleExportRecap = useCallback(() => {
    exportSeasonRecap(activeSave, currentSalaryData);
  }, [activeSave, currentSalaryData]);

  const handleCreateSave = useCallback((name, opts) => {
    setSaves(prev => [...prev, getDefaultSave(name, opts)]);
    setActiveIndex(saves.length);
  }, [saves.length]);

  const handleDeleteSave = useCallback((i) => {
    setSaves(prev => {
      const next = prev.filter((_, idx) => idx !== i);
      return next.length === 0 ? [getDefaultSave('My GM Season')] : next;
    });
    setActiveIndex(prev => prev >= i ? Math.max(0, prev - 1) : prev);
  }, []);

  const handleRenameSave = useCallback((i, newName) => {
    setSaves(prev => {
      const next = [...prev];
      next[i] = { ...next[i], name: newName };
      return next;
    });
  }, []);

  const handleReset = useCallback(() => {
    if (window.confirm('Reset this save to real-life rosters? All trades will be undone.')) {
      updateActiveSave({
        salaryData: JSON.parse(JSON.stringify(SALARY_DATA)),
        tradeHistory: [],
      });
    }
  }, [updateActiveSave]);

  const handleAdvancePhase = useCallback(() => {
    const phases = ['regular', 'postDeadline', 'playoffs', 'draft', 'offseason'];
    const idx = phases.indexOf(seasonPhase);
    if (idx < phases.length - 1) {
      const nextPhase = phases[idx + 1];
      const sd = JSON.parse(JSON.stringify(currentSalaryData || SALARY_DATA));
      const dc = activeSave.depthCharts || {};
      const simAllGames = (schedule) => {
        let u = [...schedule];
        const maxDay = Math.max(...u.map(g => g.gameDay));
        for (let d = 1; d <= maxDay; d++) {
          const dg = u.filter(g => g.gameDay === d && !g.played);
          if (dg.length > 0) {
            const simmed = dg.map(g => {
              if (g.played) return g;
              return { ...g, played: true, result: quickSim(g.home, g.away, sd, dc) };
            });
            const m = new Map(simmed.map(g => [g.id, g]));
            u = u.map(g => m.has(g.id) ? m.get(g.id) : g);
          }
        }
        return u;
      };

      // Helper: sim games up to N my-team games
      const simToMyGames = (schedule, target) => {
        let u = [...schedule];
        const myTeam = activeSave.myTeam;
        const maxDay = Math.max(...u.map(g => g.gameDay));
        let myGames = u.filter(g => g.played && (g.home === myTeam || g.away === myTeam)).length;
        for (let d = 1; d <= maxDay && myGames < target; d++) {
          const dg = u.filter(g => g.gameDay === d && !g.played);
          if (dg.length > 0) {
            const simmed = dg.map(g => {
              if (g.played) return g;
              return { ...g, played: true, result: quickSim(g.home, g.away, sd, dc) };
            });
            const m = new Map(simmed.map(g => [g.id, g]));
            u = u.map(g => m.has(g.id) ? m.get(g.id) : g);
            myGames = u.filter(g => g.played && (g.home === myTeam || g.away === myTeam)).length;
          }
        }
        return u;
      };

      // Helper: auto-sim full playoffs
      const simPlayoffs = (standings) => {
        const seeds = getPlayoffSeeds(standings);
        const po = { seeds, playIn: { east: null, west: null }, rounds: { east: [[], [], []], west: [[], [], []] }, finals: null, champion: null };

        // Play-in
        for (const c of ['east', 'west']) {
          const pi = seeds[c].playIn;
          if (pi.length >= 4) {
            const g78 = quickSim(pi[0].abbr, pi[1].abbr, sd, dc);
            const g910 = quickSim(pi[2].abbr, pi[3].abbr, sd, dc);
            const l78 = g78.winner === pi[0].abbr ? pi[1].abbr : pi[0].abbr;
            const gF = quickSim(l78, g910.winner, sd, dc);
            po.playIn[c] = { game78: g78, game910: g910, finalGame: gF, seed7: g78.winner, seed8: gF.winner };
          }
        }

        // Build first round matchups
        for (const c of ['east', 'west']) {
          const top6 = seeds[c].top6.map(t => t.abbr);
          const s7 = po.playIn[c]?.seed7 || seeds[c].playIn[0]?.abbr;
          const s8 = po.playIn[c]?.seed8 || seeds[c].playIn[2]?.abbr;
          const bracket = [...top6, s7, s8].filter(Boolean);
          const matchups = [[0, 7], [1, 6], [2, 5], [3, 4]];
          po.rounds[c][0] = matchups.map(([a, b]) => {
            if (!bracket[a] || !bracket[b]) return null;
            let hW = 0, aW = 0;
            while (hW < 4 && aW < 4) {
              const r = quickSim(bracket[a], bracket[b], sd, dc);
              if (r.winner === bracket[a]) hW++; else aW++;
            }
            return { home: bracket[a], away: bracket[b], homeWins: hW, awayWins: aW, winner: hW === 4 ? bracket[a] : bracket[b], loser: hW === 4 ? bracket[b] : bracket[a] };
          }).filter(Boolean);
        }

        // Sim rounds 2, 3 (conf semis, conf finals)
        for (const c of ['east', 'west']) {
          for (let round = 1; round <= 2; round++) {
            const prev = po.rounds[c][round - 1];
            const winners = prev.map(s => s.winner);
            const next = [];
            for (let i = 0; i < winners.length; i += 2) {
              if (!winners[i] || !winners[i + 1]) continue;
              let hW = 0, aW = 0;
              while (hW < 4 && aW < 4) {
                const r = quickSim(winners[i], winners[i + 1], sd, dc);
                if (r.winner === winners[i]) hW++; else aW++;
              }
              next.push({ home: winners[i], away: winners[i + 1], homeWins: hW, awayWins: aW, winner: hW === 4 ? winners[i] : winners[i + 1], loser: hW === 4 ? winners[i + 1] : winners[i] });
            }
            po.rounds[c][round] = next;
          }
        }

        // Finals
        const eastChamp = po.rounds.east[2]?.[0]?.winner;
        const westChamp = po.rounds.west[2]?.[0]?.winner;
        if (eastChamp && westChamp) {
          let hW = 0, aW = 0;
          while (hW < 4 && aW < 4) {
            const r = quickSim(eastChamp, westChamp, sd, dc);
            if (r.winner === eastChamp) hW++; else aW++;
          }
          po.finals = { home: eastChamp, away: westChamp, homeWins: hW, awayWins: aW, winner: hW === 4 ? eastChamp : westChamp, loser: hW === 4 ? westChamp : eastChamp };
          po.champion = po.finals.winner;
        }
        return po;
      };

      if (isSeasonMode && activeSave.seasonState?.schedule) {
        let aiTradeLog = [...(activeSave.aiTradeLog || [])];
        let pendingOffers = [...(activeSave.pendingTradeOffers || [])];
        let newsFeed = [...(activeSave.newsFeed || [])];
        const sYear = activeSave.seasonYear || 2025;

        // Generate AI trades at trade deadline
        if (nextPhase === 'postDeadline' && activeSave.aiTradesEnabled !== false) {
          const totalTarget = 17 + Math.floor(Math.random() * 9);
          let allTrades = [];
          for (let batch = 0; batch < 15 && allTrades.length < totalTarget; batch++) {
            const batchTrades = generateAITrades(sd, activeSave.myTeam, sYear);
            allTrades = [...allTrades, ...batchTrades];
          }
          allTrades = allTrades.slice(0, totalTarget);

          if (allTrades.length > 0) {
            const executed = executeAITrades(allTrades, sd);
            aiTradeLog = [...aiTradeLog, ...executed];
            for (const t of executed) {
              logTransaction('ai_trade', {
                team1: t.team1, team2: t.team2,
                team1Sends: t.team1Sends.map(p => p.name),
                team2Sends: t.team2Sends.map(p => p.name),
                type: t.type,
                summary: `${t.team1} traded ${t.team1Sends.map(p => p.name).join(', ')} to ${t.team2} for ${t.team2Sends.map(p => p.name).join(', ')}`,
              });
              const team1Info = getTeam(t.team1);
              const team2Info = getTeam(t.team2);
              newsFeed.push({
                id: `news-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
                type: t.type === 'blockbuster' ? 'blockbuster_trade' : 'trade',
                headline: t.type === 'blockbuster'
                  ? `BREAKING: ${team1Info?.name || t.team1} and ${team2Info?.name || t.team2} complete blockbuster deal`
                  : `${team1Info?.name || t.team1} acquire ${t.team2Sends.map(p => p.name).join(', ')} from ${team2Info?.name || t.team2}`,
                body: `The ${team1Info?.name || t.team1} have traded ${t.team1Sends.map(p => p.name).join(' and ')} to the ${team2Info?.name || t.team2} in exchange for ${t.team2Sends.map(p => p.name).join(' and ')}.${t.type === 'blockbuster' ? ' League sources describe the deal as a significant move.' : ''}`,
                teams: [t.team1, t.team2],
                timestamp: Date.now() - Math.floor(Math.random() * 86400000),
              });
            }
          }
        }

        // Counter offers at every applicable phase (targeting 5-10 per season)
        if (activeSave.aiTradesEnabled !== false && (nextPhase === 'postDeadline' || nextPhase === 'playoffs' || nextPhase === 'offseason')) {
          const newOffers = generateCounterOffers(sd, activeSave.myTeam, sYear);
          if (newOffers.length > 0) {
            pendingOffers = [...pendingOffers, ...newOffers];
          }
        }

        if (nextPhase === 'postDeadline') {
          const u = simToMyGames(activeSave.seasonState.schedule, 50);
          updateActiveSave({
            seasonPhase: nextPhase,
            salaryData: sd,
            aiTradeLog,
            pendingTradeOffers: pendingOffers,
            newsFeed: newsFeed.sort((a, b) => b.timestamp - a.timestamp),
            seasonState: { ...activeSave.seasonState, schedule: u },
          });
          return;
        }

        if (nextPhase === 'playoffs') {
          const u = simAllGames(activeSave.seasonState.schedule);
          updateActiveSave({
            seasonPhase: nextPhase,
            salaryData: sd,
            aiTradeLog,
            pendingTradeOffers: pendingOffers,
            seasonState: { ...activeSave.seasonState, schedule: u },
          });
          setPage('season');
          return;
        }

        if (nextPhase === 'draft') {
          let schedule = activeSave.seasonState.schedule;
          const hasUnplayed = schedule.some(g => !g.played);
          if (hasUnplayed) {
            schedule = simAllGames(schedule);
          }
          const standings = computeStandings(schedule);
          const playoffs = activeSave.seasonState.playoffs || simPlayoffs(standings);
          const allTeamRecords = [...(standings.east || []), ...(standings.west || [])];
          const finalStandings = { east: standings.east || allTeamRecords.slice(0, 15), west: standings.west || allTeamRecords.slice(15) };

          updateActiveSave({
            seasonState: { ...activeSave.seasonState, schedule, playoffs, finalStandings },
            seasonPhase: 'draft',
            draftState: { currentPick: 0, results: [] },
          });
          setPage('draft');
          return;
        }

        // Offseason — save trade data before handleBeginOffseason takes over
        if (nextPhase === 'offseason') {
          updateActiveSave({
            salaryData: sd,
            pendingTradeOffers: pendingOffers,
            aiTradeLog,
            newsFeed: newsFeed.sort((a, b) => b.timestamp - a.timestamp),
          });
        }
      }

      if (nextPhase === 'draft') {
        handleBeginDraft();
      } else if (nextPhase === 'offseason') {
        handleBeginOffseason(null);
      } else {
        updateActiveSave({ seasonPhase: nextPhase });
      }
    }
  }, [seasonPhase, updateActiveSave, handleBeginOffseason, handleBeginDraft, isSeasonMode, activeSave, currentSalaryData, logTransaction]);

  const handleRevertPhase = useCallback(() => {
    const phases = ['regular', 'postDeadline', 'playoffs', 'draft', 'offseason'];
    const idx = phases.indexOf(seasonPhase);
    if (idx > 0) {
      updateActiveSave({ seasonPhase: phases[idx - 1] });
    }
  }, [seasonPhase, updateActiveSave]);

  let content;
  if (page === 'teams' && selectedTeam) content = <TeamDetail teamAbbr={selectedTeam} onBack={handleBack} standings={standings} salaryData={currentSalaryData} onWaivePlayer={handleWaivePlayer} onExtendPlayer={handleExtendPlayer} tradeHistory={tradeHistory} onOpenDepthChart={(abbr) => setDepthChartOpen(abbr)} />;
  else if (page === 'teams') content = standingsLoading ? <Loading message="Loading league data..." /> : <TeamBrowser onSelectTeam={handleSelectTeam} standings={standings} salaryData={currentSalaryData} />;
  else if (page === 'trade') content = (
    <TradeMachine
      allTeams={TEAMS}
      salaryData={currentSalaryData}
      onTradeExecuted={handleTradeExecuted}
      tradeHistory={tradeHistory}
      seasonPhase={seasonPhase}
      myTeam={activeControlTeam}
      mode={activeSave.mode || 'sandbox'}
    />
  );
  else if (page === 'draft' || page === 'draftSim') {
    const inDraftPhase = seasonPhase === 'draft' && activeSave.draftState;
    content = inDraftPhase ? (
      <DraftSim
        myTeam={activeControlTeam}
        salaryData={currentSalaryData}
        standings={activeSave.seasonState?.finalStandings || activeSave.seasonState?.prevSeasonStandings || null}
        draftState={{...(activeSave.draftState || {}), pickOwnerOverrides: activeSave.draftPickOverrides || {}}}
        tradeHistory={tradeHistory}
        onDraftPick={handleDraftPick}
        onDraftComplete={handleDraftComplete}
        onOpenTradeMachine={handleDraftOpenTrade}
        onProgress={handleDraftProgress}
        seasonYear={activeSave.seasonYear || 2025}
      />
    ) : (
      <DraftBoard salaryData={currentSalaryData} seasonYear={activeSave.seasonYear || 2025} />
    );
  }
  else if (page === 'search') content = <PlayerSearch salaryData={currentSalaryData} onSelectTeam={(abbr) => { setSelectedTeam(abbr); setPage('teams'); }} />;
  else if (page === 'history') content = <TransactionHistory transactionLog={activeSave.transactionLog} />;
  else if (page === 'season' && isSeasonMode) content = (
    <SeasonHub
      myTeam={activeSave.myTeam}
      seasonState={activeSave.seasonState}
      salaryData={currentSalaryData}
      depthCharts={activeSave.depthCharts || {}}
      onUpdateSeason={handleUpdateSeason}
      onPhaseChange={(phase) => {
        if (activeSave.seasonPhase !== phase) updateActiveSave({ seasonPhase: phase });
      }}
      onBeginFreeAgency={(standings) => {
        const seasonState = activeSave.seasonState || {};
        updateActiveSave({
          seasonState: { ...seasonState, finalStandings: standings || null },
        });
        handleBeginDraft();
      }}
    />
  );
  else if (page === 'freeAgency') content = (
    <FreeAgencyBoard
      freeAgentPool={currentFreeAgentPool}
      salaryData={currentSalaryData}
      myTeam={activeControlTeam}
      onSignPlayer={handleSignPlayer}
      onAdvanceSeason={seasonPhase === 'offseason' ? handleAdvanceSeason : null}
      onBack={() => setPage(isSeasonMode ? 'season' : 'teams')}
      isMidSeason={seasonPhase === 'regular' || seasonPhase === 'postDeadline'}
      seasonYear={activeSave.seasonYear || 2025}
    />
  );

  return (
    <div className="app">
      <Header onHome={handleHome} page={page} onNav={handleNav} theme={theme} onToggleTheme={toggleTheme} isSeasonMode={isSeasonMode} seasonPhase={seasonPhase} controlTeam={controlTeam} onChangeTeam={!isSeasonMode ? setControlTeam : null}>
        <SaveBar
          saves={saves}
          activeIndex={activeIndex}
          onSelect={handleSelectSave}
          onCreate={() => setShowNewSaveModal(true)}
          onDelete={handleDeleteSave}
          onRename={handleRenameSave}
          onReset={handleReset}
        />
      </Header>
      {isSeasonMode && (
        <div className="season-sim-banner">
          <span className="season-banner-badge">BETA</span>
          <span className="season-banner-text">Season Sim Mode</span>
          <span className="season-banner-divider">·</span>
          <span className="season-banner-hint">Stats and outcomes are simulated, not real</span>
          <button className="season-banner-info" onClick={() => handleNav('teams')} title="Switch to GM Mode for real-time data">
            Want real data? Use GM Mode →
          </button>
        </div>
      )}
      <SeasonPhaseBar
        phase={seasonPhase}
        mode={activeSave.mode || 'sandbox'}
        onAdvance={handleAdvancePhase}
        onRevert={handleRevertPhase}
        seasonYear={activeSave.seasonYear || 2025}
        aiTradesEnabled={activeSave.aiTradesEnabled !== false}
        onToggleAITrades={() => updateActiveSave({ aiTradesEnabled: !activeSave.aiTradesEnabled })}
        aiTradeLog={activeSave.aiTradeLog || []}
        myTeamExtensions={(() => {
          const team = activeSave.myTeam;
          const sd = currentSalaryData;
          if (!team || !sd?.[team]?.players) return { rookie: 0, veteran: 0, expiring: 0, players: [] };
          const counts = { rookie: 0, veteran: 0, expiring: 0, players: [] };
          for (const p of sd[team].players) {
            const ext = getExtensionStatus(p);
            if (ext) {
              counts[ext.type] = (counts[ext.type] || 0) + 1;
              counts.players.push({ name: p.name, type: ext.type, label: ext.label });
            }
          }
          return counts;
        })()}
      />
      <div className={`app-layout ${isSeasonMode && page === 'teams' && !selectedTeam && showNewsSidebar && ((activeSave.aiTradeLog || []).length > 0 || (activeSave.newsFeed || []).length > 0 || (activeSave.transactionLog || []).length > 0) ? 'with-sidebar' : ''}`}>
        <div className="main">{content}</div>
        {isSeasonMode && page === 'teams' && !selectedTeam && showNewsSidebar && ((activeSave.aiTradeLog || []).length > 0 || (activeSave.newsFeed || []).length > 0 || (activeSave.transactionLog || []).length > 0) && (
          <LeagueNewsSidebar
            aiTradeLog={activeSave.aiTradeLog || []}
            transactionLog={activeSave.transactionLog || []}
            newsFeed={activeSave.newsFeed || []}
            onClose={() => setShowNewsSidebar(false)}
          />
        )}
      </div>
      {/* News toggle + Export Recap buttons */}
      {isSeasonMode && (
        <div className="season-action-bar">
          {pendingOffers.length > 0 && (
            <button className="season-action-btn offers" onClick={() => setShowCounterOffers(true)}>
              Trade Offers ({pendingOffers.length})
            </button>
          )}
          {(activeSave.seasonState?.playoffs?.champion || (activeSave.seasonYear || 2025) > 2025) && (
            <button className="season-action-btn export" onClick={handleExportRecap}>
              Export Season Recap
            </button>
          )}
        </div>
      )}
      <Footer />

      {/* Counter Offer Modal */}
      {showCounterOffers && pendingOffers.length > 0 && isSeasonMode && (
        <CounterOfferModal
          offers={pendingOffers}
          onAccept={handleAcceptOffer}
          onDecline={handleDeclineOffer}
          myTeam={activeSave.myTeam}
        />
      )}

      {/* Floating Help Button + Tooltip */}
      <div className="help-fab-wrap">
        {showHelpTip && (
          <div className="help-tip-bubble" onClick={() => { dismissHelpTip(); setShowHelp(true); }}>
            <div className="help-tip-text">Need help? Click here for a guide to the current page.</div>
            <button className="help-tip-dismiss" onClick={(e) => { e.stopPropagation(); dismissHelpTip(); }}>&times;</button>
          </div>
        )}
        <button className="help-fab" onClick={() => { setShowHelp(true); dismissHelpTip(); }} title="Need help?">?</button>
      </div>

      {/* Contextual Help Modal */}
      {showHelp && <ContextualHelp page={page} onClose={() => { setShowHelp(false); setHelpStartTutorial(false); }} startTutorial={helpStartTutorial} />}
      {showNewSaveModal && (
        <NewSaveModal
          onClose={() => setShowNewSaveModal(false)}
          onCreate={handleCreateSave}
        />
      )}
      {showWelcome && (
        <div className="modal-overlay" onClick={dismissWelcome}>
          <div className="welcome-modal" onClick={(e) => e.stopPropagation()}>
            <div className="welcome-header">
              <img src="/hft_logo.png" alt="" className="welcome-logo" />
              <div className="welcome-title">Hoops For Thought</div>
              <div className="welcome-subtitle">NBA GM Simulator</div>
              <div className="welcome-beta-tag">Beta</div>
            </div>

            <div className="welcome-intro">
              Step into the front office and manage an NBA team using real rosters, real salaries, and the actual 2025-26 CBA rules. Every trade, signing, and roster decision is validated against the same salary cap framework the league uses.
            </div>

            <div className="welcome-section-title">Two Ways to Play</div>
            <div className="welcome-modes">
              <div className="welcome-mode-card gm">
                <div className="welcome-mode-name">GM Mode</div>
                <div className="welcome-mode-tag recommended">Recommended</div>
                <div className="welcome-mode-desc">Explore real trade scenarios, manage any team's cap sheet, sign free agents, and run a full offseason. Use the team selector in the header to switch between all 30 teams.</div>
              </div>
              <div className="welcome-mode-card season">
                <div className="welcome-mode-name">Season Sim</div>
                <div className="welcome-mode-tag beta">Beta</div>
                <div className="welcome-mode-desc">Pick a team and simulate an entire season from opening night through the NBA Finals, then run the offseason, draft, and advance to the next year.</div>
              </div>
            </div>

            <div className="welcome-section-title">What You Can Do</div>
            <div className="welcome-features">
              <div className="welcome-feature">
                <div className="welcome-feature-name">Trade Machine</div>
                <div className="welcome-feature-desc">Build trades between 2 or 3 teams with full CBA validation: salary matching, hard caps, apron rules, and the no-aggregation rule for second apron teams.</div>
              </div>
              <div className="welcome-feature">
                <div className="welcome-feature-name">Free Agency</div>
                <div className="welcome-feature-desc">Sign free agents to your team's roster. Cap space, mid-level exceptions, and minimum contracts are all enforced. During the offseason, expiring contracts automatically hit the market.</div>
              </div>
              <div className="welcome-feature">
                <div className="welcome-feature-name">Draft Simulator</div>
                <div className="welcome-feature-desc">Run a full 2-round, 60-pick draft. Sim to your pick, make your selection, or trade the pick. AI teams draft based on positional need and prospect rankings.</div>
              </div>
              <div className="welcome-feature">
                <div className="welcome-feature-name">Roster Management</div>
                <div className="welcome-feature-desc">View every team's full salary breakdown across 4 years. Waive players with dead cap implications, stretch provisions, and execute contract extensions.</div>
              </div>
              <div className="welcome-feature">
                <div className="welcome-feature-name">Search and History</div>
                <div className="welcome-feature-desc">Find any player across all 30 teams instantly. Every trade, signing, waiver, draft pick, and extension is logged in a full transaction history.</div>
              </div>
              <div className="welcome-feature">
                <div className="welcome-feature-name">AI League Trades <span className="beta-tag">BETA</span></div>
                <div className="welcome-feature-desc">When enabled in Season Sim, teams around the league will make moves throughout the season. You may receive trade proposals from other GMs that you can accept or decline.</div>
              </div>
              <div className="welcome-feature">
                <div className="welcome-feature-name">Player Progression</div>
                <div className="welcome-feature-desc">Player ratings evolve between seasons based on real-world trajectory expectations. Young stars develop, prime players hold steady, and veterans transition into new roles.</div>
              </div>
            </div>

            <div className="welcome-footer-note">
              This is a beta release. Some features are still being refined and new ones are on the way. If you encounter any issues or have feedback, we would love to hear from you.
            </div>
            <div className="welcome-footer-note" style={{marginTop: 8}}>
              Thank you for trying Hoops For Thought. We built this for people who love the front-office side of basketball as much as the game itself. We hope you enjoy it.
            </div>

            <div className="welcome-dismiss-row">
              <label className="welcome-checkbox-label">
                <input type="checkbox" checked={dontShowAgain} onChange={e => setDontShowAgain(e.target.checked)} />
                <span>Don't show this again</span>
              </label>
            </div>
            <div className="welcome-btn-row">
              <button className="welcome-tour-btn" onClick={() => { dismissWelcome(); setHelpStartTutorial(true); setShowHelp(true); }}>Take a Tour</button>
              <button className="welcome-dismiss" onClick={dismissWelcome}>Get Started</button>
            </div>
          </div>
        </div>
      )}

      {/* Progression Popup — shows team changes when new season starts */}
      {showProgressionPopup && activeSave.lastProgressionReport && (() => {
        const myTeam = activeSave.myTeam;
        const report = activeSave.lastProgressionReport || [];
        const myTeamChanges = report.filter(p => p.team === myTeam).sort((a, b) => b.change - a.change);
        const retirements = activeSave.lastRetirements || [];
        const sYear = activeSave.seasonYear || 2026;
        return (
          <div className="modal-overlay" onClick={() => setShowProgressionPopup(false)}>
            <div className="modal-content" style={{ maxWidth: 520, padding: 24 }} onClick={e => e.stopPropagation()}>
              <button className="modal-close" onClick={() => setShowProgressionPopup(false)}>&times;</button>
              <div className="section-label" style={{ marginBottom: 16 }}>{sYear}-{String(sYear + 1).slice(2)} Season Preview</div>
              {myTeamChanges.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 8, color: 'var(--accent-gold)' }}>Your Team Progression</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto', gap: '4px 12px', fontSize: 12 }}>
                    <div style={{ fontWeight: 600, fontSize: 10, color: 'var(--text-dim)' }}>PLAYER</div>
                    <div style={{ fontWeight: 600, fontSize: 10, color: 'var(--text-dim)' }}>OLD</div>
                    <div style={{ fontWeight: 600, fontSize: 10, color: 'var(--text-dim)' }}>NEW</div>
                    <div style={{ fontWeight: 600, fontSize: 10, color: 'var(--text-dim)' }}>CHG</div>
                    {myTeamChanges.map((p, i) => (
                      <React.Fragment key={i}>
                        <div style={{ fontWeight: 500 }}>{p.name}</div>
                        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-dim)' }}>{p.oldOvr}</div>
                        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: getRatingColor(p.newOvr) }}>{p.newOvr}</div>
                        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: p.change > 0 ? 'var(--accent-green)' : 'var(--accent-red)' }}>{p.change > 0 ? '+' : ''}{p.change}</div>
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              )}
              {retirements.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 8, color: '#8B6914' }}>Retirements</div>
                  {retirements.map((r, i) => (
                    <div key={i} style={{ fontSize: 12, padding: '3px 0', color: 'var(--text-muted)' }}>
                      {r.name} (age {r.age}) - Thank you for everything.
                    </div>
                  ))}
                </div>
              )}
              {myTeamChanges.length === 0 && retirements.length === 0 && (
                <div style={{ fontSize: 13, color: 'var(--text-dim)', textAlign: 'center', padding: 20 }}>
                  No notable changes this offseason.
                </div>
              )}
              <button className="save-btn" style={{ width: '100%', marginTop: 12 }} onClick={() => setShowProgressionPopup(false)}>Start Season</button>
            </div>
          </div>
        );
      })()}

      {/* Depth Chart Modal — rendered at App level to escape stacking context */}
      {depthChartOpen && (() => {
        const dcRoster = (currentSalaryData[depthChartOpen]?.players || []);
        return (
          <div className="modal-overlay" onClick={() => setDepthChartOpen(null)}>
            <div className="modal-content" style={{ width: '95%', maxWidth: 1100, padding: 24 }} onClick={e => e.stopPropagation()}>
              <button className="modal-close" onClick={() => setDepthChartOpen(null)}>&times;</button>
              <DepthChart
                teamAbbr={depthChartOpen}
                roster={dcRoster}
                depthChart={(activeSave.depthCharts || {})[depthChartOpen] || null}
                onSave={(abbr, chart) => {
                  handleSaveDepthChart(abbr, chart);
                  setDepthChartOpen(null);
                }}
                onClose={() => setDepthChartOpen(null)}
              />
            </div>
          </div>
        );
      })()}

      {/* Draft Trade Machine Modal — opens full trade machine during draft */}
      {draftTradeOpen && seasonPhase === 'draft' && (
        <div className="modal-overlay" onClick={handleDraftCloseTrade}>
          <div className="modal-content" style={{ width: '95%', maxWidth: 1200, padding: 0 }} onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={handleDraftCloseTrade}>&times;</button>
            <div style={{ padding: '16px 20px 0' }}>
              <TradeMachine
                allTeams={TEAMS}
                myTeam={activeControlTeam}
                salaryData={currentSalaryData}
                onTradeExecuted={(newData, tradePkgs) => {
                  handleTradeExecuted(newData, tradePkgs);
                  handleDraftCloseTrade();
                }}
                tradeHistory={tradeHistory}
                seasonPhase="regular"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
